/**
 * TimeReviewModal — manager time review + approval tool (Round 15).
 *
 * Faithful 1-to-1 React port of the old Softr `examples/time_and_expense_reviewer.html`. The original
 * imperative DOM-building logic is preserved verbatim inside `runTimeReview()`; only the data seams
 * were swapped:
 *   - hardcoded Airtable PAT + direct api.airtable.com calls → services/timecards.js (/api/airtable)
 *   - PROXY_URL fetch (BQE Core post) with x-aj-token          → tc.postTimeEntryToCore (Clerk bearer)
 *   - notify-rejection fetch with x-aj-token                   → tc.notifyRejection (Clerk bearer)
 *   - document.getElementById('aj-mgr')                        → the modal's container ref
 * The openMS document listener, the focus listener and the poll interval are torn down on close.
 * CSS (scoped to #aj-mgr) is imported verbatim from timereview.css.
 *
 * Opens on the `open-timecard-review` CustomEvent. Gated to hasModule('timecard-admin') by the caller.
 */
import { useEffect, useRef, useState } from 'react'
import './timereview.css'
import * as tc from '../services/timecards.js'
import { ctx } from '../outline/ctx.js'
import { getMyReviewScope } from '../services/timeGate.js'
import { openSchedulePeek } from './schedulePeek.js'
import { confirmDialog, alertDialog, promptDialog } from './Dialog.jsx'
import { startDirectMessage, setMemberSlackId } from '../services/slack.js'
import { skillsForTimeEntry } from '../outline/eligibility.js'
import { ebrBand, ebrLabel } from './ebrBands.js'
import { getReviewContext } from '../services/activity.js'
import { listExpenseReports } from '../services/expenseReports.js'

export default function TimeReviewModal() {
  const [open, setOpen] = useState(false)
  const [showKbd, setShowKbd] = useState(false)
  const rootRef = useRef(null)
  const presetRef = useRef(null)   // { memberId?, statuses? } from the open event (e.g. time-gate launch)

  useEffect(() => {
    function onOpen(e) { presetRef.current = (e && e.detail) || null; setOpen(true) }
    // Expense Review is its own window; opening it closes this one so the two never stack (their
    // keyboard shortcuts would both fire).
    function onExpenses() { setOpen(false) }
    window.addEventListener('open-timecard-review', onOpen)
    window.addEventListener('open-expense-review', onExpenses)
    return () => { window.removeEventListener('open-timecard-review', onOpen); window.removeEventListener('open-expense-review', onExpenses) }
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    let cleanup = () => {}
    try { cleanup = runTimeReview(rootRef.current, presetRef.current) } catch (e) { console.error(e) }
    return () => {
      document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; try { cleanup() } catch (e) {}
      // Re-evaluate the time gate after a review session (the reviewer gate clears as entries are actioned).
      window.dispatchEvent(new Event('time-gate-recheck'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null
  return (
    <div className="tr-overlay">
      <div className="tr-modal">
        <div className="tr-modal-bar">
          <span className="tr-modal-title">Time Review</span>
          <div className="tr-bar-right">
            <button className="tr-kbd-btn" title="Keyboard navigation" onClick={() => setShowKbd(v => !v)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
              </svg>
              Keyboard
            </button>
            <button className="tr-modal-close" title="Close (Esc)" onClick={() => setOpen(false)}>&times;</button>
          </div>
          {showKbd && (
            <>
              <div className="tr-kbd-backdrop" onClick={() => setShowKbd(false)} />
              <div className="tr-kbd-pop" role="dialog" aria-label="Keyboard navigation">
                <div className="tr-kbd-pop-title">Keyboard navigation</div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>↑</kbd><kbd>↓</kbd></span><span>Move between entries</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>A</kbd></span><span>Approve focused entry</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>R</kbd></span><span>Reject focused entry</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>E</kbd></span><span>Edit focused entry</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>↵</kbd></span><span>Activate the focused button</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>Tab</kbd></span><span>Next control</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>Esc</kbd></span><span>Close</span></div>
              </div>
            </>
          )}
        </div>
        <div className="tr-modal-scroll">
          <div id="aj-mgr" ref={rootRef}></div>
        </div>
      </div>
    </div>
  )
}

// ── Ported tool ───────────────────────────────────────────────────────────────
// Verbatim logic from examples/time_and_expense_reviewer.html, data seams routed through
// services/timecards.js. Returns a cleanup function that removes all document/window listeners.
function runTimeReview(container, preset) {
  if (!container) return () => {}

  var TBL_STAGING = 'tblksH7rbKDI4gWeZ'; // label only — the service owns the real table id
  var ACTIVITY_CODES = [];

  // Data-seam shims (the rest of the tool is unchanged).
  function atPatch(tableId, recordId, fields) { return tc.patchEntry(recordId, fields); }
  function atDelete(tableId, recordId) { return tc.deleteEntry(recordId); }

  // ── State ──
  var allRecords   = [], allMembers = [], allPhases = [];
  var targetEBR = 125;
  var weekOffset   = 0;
  // Billed Fee ↔ Core sync ("Run now" — mirrors the nightly cron, api/sync-billed-fee.js).
  var billedFeeSyncing = false, billedFeeSyncMsg = null, billedFeeLastRunAt = null;
  // Preset filters (e.g. opened from the time-gate's "Open Time Review" for a specific member).
  var filterMembers = (preset && preset.memberId) ? [preset.memberId] : [];
  var filterStatuses = (preset && Array.isArray(preset.statuses)) ? preset.statuses.slice() : [];
  // Preset date range (e.g. the time-gate "Retry in Time Review" passes the oldest failed date so
  // older Post-Failed entries load, not just the current week).
  var filterDateFrom = (preset && preset.from) ? preset.from : '', filterDateTo = (preset && preset.to) ? preset.to : '';
  // Launched from the time-gate (a preset filter) → expand the filtered entries and auto-select the
  // top one, so the reviewer can drive with the keyboard immediately without clicking in.
  var autoExpand = !!(preset && (preset.memberId || (preset.statuses && preset.statuses.length) || preset.from || preset.to));
  var editingId = null, rejectingId = null;
  var workItemsCache = {};   // submitterMemberId → [work items] for the entity-attribution dropdown
  var focusedRid = null;   // keyboard-focused entry (↑/↓ navigation + a/r/e hotkeys)
  var postingIds = {};
  var collapsedDays = {};   // top-level day-group collapse state
  var summaryCollapsed = false;
  var collapsedGroups = {};   // person-, project-, and activity-code-level collapse state, keyed per buildLevelGroup()
  var lastActionTime = 0;
  var ACTION_GRACE = 15000;
  // Expenses are reviewed by whoever reviews that person's time (Matt, 2026-10-25), in Expense Review
  // (ExpenseReviewModal.jsx) since 2026-10-28; here only the count of the reviewer's own queue.
  var pendingExpenses = [], expensesLoaded = false, expensesError = null;
  var selectedIds = {};
  var batchRejectOpen = false;
  var needsAttentionView = false;
  var needsAttentionRecords = [];
  var needsAttentionFetching = false;
  // Round 16.3 — reviewer sees assigned members; admin (practice principal) sees everyone.
  var reviewScope = { reviewees: new Set(), coveredAll: new Set(), isAdmin: false, loaded: false };
  function inReviewScope(sid){
    if (!reviewScope.loaded) return true;        // before scope loads, don't hide everything
    if (!sid) return false;
    if (reviewScope.reviewees.has(sid)) return true;
    if (reviewScope.isAdmin) return true;        // admin sees all members regardless of assignment
    return false;
  }
  var fetchInFlight = false, fetchingNow = false, lastFetchTime = null, lastFetchError = null;

  // ── EBR helpers — banding logic lives in ebrBands.js (shared with phaseLandscape.js); this file
  // just maps a band to its own theme-reactive CSS vars (scoped to #aj-mgr, see timereview.css). ──
  function ebrColor(ratio, isFixedFee) {
    var band = ebrBand(ratio, isFixedFee);
    if(!band) return 'var(--text-soft)';
    if(band==='red-high'||band==='red-low') return 'var(--red)';
    if(band==='amber-high'||band==='amber-low') return 'var(--amber)';
    return 'var(--green)';
  }
  // Canonical isFixedFee check — mirrors phaseFinancials.js#computeLiveFinancials exactly. See the
  // note on ebrBands.js#ebrBand for why this branch matters here specifically: for Fixed Fee, EBR
  // is a budget-PACE ratio (Contracted Fee ÷ Hours), not a billing rate, so the "senior/junior
  // staffing mix" framing below is only ever coherent for Hourly. Caught live 2026-08-10 on Forsyth
  // Residence's Construction Documents Phase — EBR $335/hr against a $125 target read as
  // "significantly senior-heavy" in red, when it just meant the phase had barely touched its
  // budget yet (a good thing, not a warning).
  function isPhaseFixedFee(phData) { return !!(phData && phData.contractType!=='Hourly' && phData.contractedFee>0); }
  function ebrChargeTip(ratio, phData, entryCharge, entryHours) {
    if(ratio == null) return null;
    var isFixedFee = isPhaseFixedFee(phData);

    if(isFixedFee){
      // Every entry mechanically adds hours, which can only ever push this ratio DOWN (there's no
      // "this entry improves it" story per-entry the way there is for Hourly) — so the verdict here
      // is about the PHASE's pace, not this one entry's own rate.
      var isOverBudget = phData.feeExpended!=null && phData.feeExpended >= phData.contractedFee;
      if(isOverBudget) return { title: 'Phase over budget', body: 'Every additional hour now adds cost the fee doesn’t cover.' };
      if(ratio < 0.6) return { title: 'Phase significantly ahead of budget pace', body: 'Hours used are well ahead of what the fee supports at this point. Review scope and hours remaining before the next invoice.' };
      if(ratio < 0.9) return { title: 'Phase running ahead of budget pace', body: 'Hours used are a bit ahead of what the fee supports at this point. Worth a look.' };
      return { title: 'Phase on budget pace', body: 'Hours used are in line with, or well under, what the fee supports at this point — a high reading here is headroom, not a problem.' };
    }

    var verdict = '';
    var direction = null;
    if(entryHours > 0 && entryCharge > 0) {
      var entryRate = entryCharge / entryHours;
      var phaseEBR = phData.effectiveBillingRate;
      if(phaseEBR && isFinite(phaseEBR) && ratio !== 1 && entryRate !== phaseEBR) {
        // "Improves" means pulls the blended EBR back TOWARD target — that's only the same thing
        // as "higher entry rate" when the phase is junior-heavy (ratio<1, average needs to go UP).
        // When the phase is senior-heavy (ratio>1, average needs to come DOWN), a lower-than-blended
        // entry is what actually improves it. Caught live 2026-08-12 — Armando's $122/hr entry
        // against Agan Residence's $160/hr phase (target ~$125) read as "makes it worse" when it
        // mechanically pulls the average down, toward target.
        var towardTarget = ratio > 1 ? (entryRate < phaseEBR) : (entryRate > phaseEBR);
        direction = towardTarget ? 'improves' : 'worsens';
        verdict = towardTarget ? 'This entry improves it.' : 'This entry makes it worse.';
      }
    }
    var w=function(body) { return verdict ? body + ' ' + verdict : body; }
    if(ratio > 1.7) return { title: 'Phase significantly senior-heavy', body: w('EBR is well above target. This entry adds cost at a rate the phase may not sustain.'), direction: direction };
    if(ratio > 1.2) return { title: 'Phase running senior-heavy',       body: w('Hours are billing at an elevated rate. Watch concentration if fee is limited.'), direction: direction };
    if(ratio < 0.6) return { title: 'Phase significantly junior-heavy', body: w('Phase EBR is significantly below target. This may reflect junior-heavy staffing, a billing gap, or both. Review staffing mix and outstanding billing before the next invoice.'), direction: direction };
    if(ratio < 0.9) return { title: 'Phase running junior-heavy',       body: w('Phase EBR is below target. This may reflect junior-heavy staffing, a billing gap, or both. Worth reviewing before the next invoice.'), direction: direction };
    return { title: 'Phase running efficiently', body: w("This entry's cost is consistent with the current billing rate mix."), direction: direction };
  }

  function fetchActivities() {
    tc.getActivities().then(function(list) { ACTIVITY_CODES = list || []; }).catch(function() {});
  }
  function fetchPhases() {
    return tc.getEntryPhases().then(function(list) { allPhases = list || []; }).catch(function() {});
  }
  // Manual "run now" for the nightly Billed Fee ↔ Core sync — lets a reviewer confirm the EBR is
  // current before approving, without leaving Time Review to find the Admin tool.
  function fetchBilledFeeStatus() {
    return tc.getBilledFeeSyncStatus().then(function(v) { billedFeeLastRunAt = v; render(); }).catch(function() {});
  }
  // "3m ago" / "2h ago" / "5d ago" / a plain date once it's been over a week — mirrors how stale
  // this reads at a glance without needing to do date math yourself.
  function fmtAgo(iso) {
    if (!iso) return 'never';
    var ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) ms = 0;
    var mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    var days = Math.floor(hrs / 24);
    if (days < 7) return days + 'd ago';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  function syncBilledFeeNow() {
    if (billedFeeSyncing) return;
    billedFeeSyncing = true; billedFeeSyncMsg = null; render();
    tc.syncBilledFee().then(function(d) {
      billedFeeSyncMsg = { ok: true, text: 'Synced · ' + d.updated + ' phase(s) updated' };
      if (d.lastRunAt) billedFeeLastRunAt = d.lastRunAt;
      return fetchPhases();
    }).catch(function(e) {
      billedFeeSyncMsg = { ok: false, text: (e && e.message) || 'Sync failed' };
    }).finally(function() {
      billedFeeSyncing = false; render();
    });
  }
  function fetchSettings() {
    return tc.getTargetEbr().then(function(v) { if (isFinite(v) && v > 0) targetEBR = v; }).catch(function() {});
  }

  // ── Searchable dropdown (edit form) ──
  var activeEditSD = null;
  function buildEditSD(container_el, items, selectedId, placeholder, onSelect) {
    var wrap = document.createElement('div'); wrap.className = 'mgr-sd-wrap';
    var input = document.createElement('input'); input.type = 'text';
    input.className = 'mgr-sd-input'; input.autocomplete = 'off';
    var selectedItem = items.find(function(i){ return i.id === selectedId; });
    input.value = selectedItem ? selectedItem.name : '';
    input.placeholder = placeholder;
    var arrow = document.createElement('span'); arrow.className = 'mgr-sd-arrow'; arrow.textContent = '▼';
    var selectedVal = selectedId;
    var closeList=function() {
      var existing = wrap.querySelector('.mgr-sd-list');
      if(existing) existing.remove();
      activeEditSD = null;
    }
    var buildList=function(q) {
      closeList();
      var list = document.createElement('div'); list.className = 'mgr-sd-list';
      var filtered = items.filter(function(i){ return !q || i.name.toLowerCase().indexOf(q.toLowerCase()) !== -1; });
      if(placeholder === 'No phase') {
        var none = document.createElement('div'); none.className = 'mgr-sd-option';
        none.textContent = '— No phase —';
        none.addEventListener('mousedown', function(e){ e.preventDefault(); selectedVal = ''; input.value = ''; onSelect(''); closeList(); });
        list.appendChild(none);
      }
      filtered.forEach(function(item) {
        var opt = document.createElement('div');
        opt.className = 'mgr-sd-option' + (item.id === selectedVal ? ' selected' : '');
        opt.textContent = item.name;
        opt.addEventListener('mousedown', function(e){
          e.preventDefault(); selectedVal = item.id; input.value = item.name;
          onSelect(item.id); closeList();
        });
        list.appendChild(opt);
      });
      if(filtered.length === 0 && placeholder !== 'No phase') {
        var empty = document.createElement('div'); empty.className = 'mgr-sd-option';
        empty.style.color = 'var(--text-soft)'; empty.textContent = 'No matches';
        list.appendChild(empty);
      }
      wrap.appendChild(list);
      activeEditSD = wrap;
    }
    input.addEventListener('focus', function(){ buildList(''); input.select(); });
    input.addEventListener('input', function(){ buildList(input.value); });
    input.addEventListener('blur', function(){ setTimeout(closeList, 150); });
    wrap.appendChild(input); wrap.appendChild(arrow);
    wrap._getValue = function(){ return selectedVal; };
    return wrap;
  }

  function findByRid(rid) {
    var els = container.querySelectorAll('[data-rid]');
    for(var i=0;i<els.length;i++){ if(els[i].getAttribute('data-rid')===rid) return els[i]; }
    return null;
  }

  function getMonday(d) { var dt=new Date(d),day=dt.getDay(); dt.setDate(dt.getDate()-day+(day===0?-6:1)); dt.setHours(0,0,0,0); return dt; }
  function addDays(d,n) { var dt=new Date(d); dt.setDate(dt.getDate()+n); return dt; }
  function fmtDate(d) { return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); }
  function fmtDisplay(d) { return new Date(d+'T00:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'}); }
  function getWeekBounds() {
    var mon=getMonday(new Date()); mon.setDate(mon.getDate()+weekOffset*7);
    return { mon:fmtDate(mon), fri:fmtDate(addDays(mon,4)), monObj:mon, friObj:addDays(mon,4) };
  }

  // ── Fetch (routed through the service) ──
  // What the app saw: each person's engaged time in Cortex per project per day, shown as context
  // under each entry (docs/ACTIVITY_HUD_PLAN.md, "Time Review context"). Never blocks anything.
  var reviewCtx=null;
  function loadReviewContext(records){
    var mem={}, from=null, to=null;
    (records||[]).forEach(function(r){
      var m=getSubmitterId(r), d=getEntryDate(r); if(!m||!d) return;
      mem[m]=1; if(!from||d<from) from=d; if(!to||d>to) to=d;
    });
    var ids=Object.keys(mem); if(!ids.length||!from) return;
    getReviewContext(ids, from, to).then(function(c){ reviewCtx=c; render(); }).catch(function(){ /* context is optional */ });
  }
  function reviewContextLine(r){
    if(!reviewCtx||!reviewCtx.recordingSince) return null;
    var day=getEntryDate(r); if(!day||day<reviewCtx.recordingSince) return null;
    var pidRaw=r.fields['Project Record UID']; var pid=Array.isArray(pidRaw)?pidRaw[0]:pidRaw; if(!pid) return null;
    var d=(reviewCtx.days||{})[getSubmitterId(r)+'|'+day]; var projects=(d&&d.projects)||{};
    var here=projects[pid]||0;
    var did=(d&&d.did&&d.did[pid])||null;
    if(here>0||did){
      var parts=[];
      if(here>0) parts.push('In Cortex that day on this project: '+here+' h');
      if(did&&did.length) parts.push('Did: '+did.join('; '));
      return { warn:false, text:parts.join(' · ') };
    }
    var code=getActivityCode(r);
    // Overhead (00-01) time in Cortex isn't tied to a project, so "none" would always mislead there.
    var none=/^00-01(?![0-9])/.test(getPhaseName(r)) ? null : { warn:false, text:'No Cortex activity on this project that day.' };
    if(!/^AS- (Design Work|Drawing Production)/.test(code)) return none;
    if((reviewCtx.aldercad||[]).indexOf(pid)===-1) return none;
    var others=Object.keys(projects).map(function(p){return [p,projects[p]];}).sort(function(a,b){return b[1]-a[1];});
    var t='No time in this project’s model that day.';
    if(others.length){ var nm=(reviewCtx.names||{})[others[0][0]]||'another project'; t+=' Most of their app time was on '+nm+' ('+others[0][1]+' h). Wrong project?'; }
    return { warn:true, text:t };
  }

  // One line per person for a day: their Cortex time and where most of it went. Context only.
  function cortexDaySummary(ds, recs){
    if(!reviewCtx||!reviewCtx.recordingSince||ds<reviewCtx.recordingSince) return null;
    var mids={}; recs.forEach(function(r){ var m=getSubmitterId(r); if(m) mids[m]=1; });
    var parts=Object.keys(mids).map(function(mid){
      var mRec=allMembers.find(function(m){return m.id===mid;});
      var nm=mRec?(mRec.fields['Member Name']||'Someone'):'Someone';
      var d=(reviewCtx.days||{})[mid+'|'+ds]; var pr=(d&&d.projects)||{};
      var keys=Object.keys(pr); var tot=keys.reduce(function(t,k){return t+pr[k];},0);
      if(!tot) return nm+' none';
      keys.sort(function(a,b){return pr[b]-pr[a];});
      var top=(reviewCtx.names||{})[keys[0]]||'one project';
      return nm+' '+(Math.round(tot*10)/10)+' h, mostly on '+top;
    }).sort();
    return parts.length ? 'Cortex that day: '+parts.join(' · ') : null;
  }

  function loadNeedsAttention() {
    needsAttentionFetching=true; render();
    tc.getNeedsAttention().then(function(recs){
      needsAttentionRecords=recs||[];
      needsAttentionFetching=false;
      render();
      loadReviewContext(needsAttentionRecords);
    }).catch(function(){
      needsAttentionFetching=false; render();
    });
  }

  function loadAll() {
    if(fetchInFlight) return;
    fetchInFlight=true; fetchingNow=true; render();
    var wb=getWeekBounds();
    var df=filterDateFrom||wb.mon, dt=filterDateTo||wb.fri;

    loadExpenses();
    Promise.all([tc.getReviewMembers(), tc.getReviewRecords(df, dt)])
      .then(function(res){
        allMembers=res[0]||[]; allRecords=res[1]||[];
        fetchInFlight=false; fetchingNow=false; lastFetchTime=new Date(); lastFetchError=null; render();
        loadReviewContext(allRecords);
      })
      .catch(function(){
        fetchInFlight=false; fetchingNow=false; lastFetchError='Could not load data';
        while(container.firstChild) container.removeChild(container.firstChild);
        var d=document.createElement('div'); d.className='mgr-loading'; d.style.color='#c0392b'; d.textContent='⚠ Could not load data'; container.appendChild(d);
      });
  }

  var expensesFetchedAt = 0;
  function loadExpenses() {
    // loadAll() runs after nearly every time-entry action; the expense queue only needs a refresh
    // once a minute (each call also reads Core's mileage rate).
    if(Date.now()-expensesFetchedAt < 60000 && !expensesError) return;
    expensesFetchedAt=Date.now();
    listExpenseReports('review').then(function(d){
      pendingExpenses=(d&&d.reports)||[]; expensesLoaded=true; expensesError=null; render();
    }).catch(function(e){
      // A failed load is not "nothing to review": say so instead of showing an empty queue.
      expensesLoaded=true; expensesError=(e&&e.message)||'Could not load expenses'; render();
    });
  }
  function fmtMoney(v){ return v==null?'—':'$'+Number(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}); }
  // Expenses have their own window now (Matt, 2026-10-28: "a little too much on one view"). Time
  // Review only says how many are waiting and opens Expense Review on the same person.
  function buildExpensesBlock(){
    var list=pendingExpenses.filter(function(x){ return !filterMembers.length || filterMembers.indexOf(x.memberId)>=0; });
    if(!expensesLoaded || expensesError || !list.length) return null;
    var total=list.reduce(function(a,x){return a+(Number(x.amount)||0);},0);
    var box=document.createElement('div'); box.className='mgr-summary';
    box.style.cssText='display:flex;align-items:center;justify-content:space-between;gap:10px;';
    var t=document.createElement('span'); t.className='mgr-summary-title';
    t.textContent=list.length+' expense'+(list.length===1?'':'s')+' also waiting · '+fmtMoney(total);
    var b=document.createElement('button'); b.className='mgr-btn'; b.textContent='Open Expense Review';
    b.addEventListener('click',function(){
      window.dispatchEvent(new CustomEvent('open-expense-review',{ detail: { memberId: filterMembers.length===1 ? filterMembers[0] : null } }));
    });
    box.appendChild(t); box.appendChild(b);
    return box;
  }

  // ── Helpers ──
  function getStatus(r){ var s=r.fields['Status']; return (typeof s==='object'&&s&&s.label)?s.label:(s||'Pending Review'); }
  function getEntryDate(r){ return (r.fields['Entry Date']||'').split('T')[0].slice(0,10); }
  function getSubmitterId(r){ var raw=r.fields['Record ID (from Submitter)']; return Array.isArray(raw)?raw[0]:(raw||''); }
  function getHours(r){ return parseFloat(r.fields['Actual Hours'])||0; }
  function isBillable(r){ return r.fields['Billable']===true; }
  function getProjectName(r){ var raw=r.fields['Project Name']; var v=Array.isArray(raw)?raw[0]:raw; return v||'(No project)'; }
  function getPhaseName(r){ var raw=r.fields['Phase Name']; var v=Array.isArray(raw)?raw[0]:raw; return v||'(No phase)'; }
  function getActivityCode(r){ var raw=r.fields['Activity Code String']; var v=Array.isArray(raw)?raw[0]:raw; return (v&&String(v).trim())||'(No activity)'; }
  function pillClass(s){ return {'Pending Review':'pill-pending','Submitted':'pill-submitted','Approved':'pill-approved','Rejected':'pill-rejected','Posted':'pill-posted'}[s]||'pill-pending'; }

  // Shared CSS classes for the four collapsible grouping levels nested under a day, indexed by
  // NESTING DEPTH (not by what's being grouped) — each tier is progressively more indented,
  // lighter-weight sibling classes than the one before it. Deliberately positional rather than
  // named per grouping (person/project/etc.) so the render loop below can reorder which field is
  // grouped at which depth (e.g. Person↔Project) without ever touching this table.
  var LEVEL_TIERS = [
    { group:'mgr-date-group', header:'mgr-date-header', headerLeft:'mgr-date-header-left', label:'mgr-date-label', total:'mgr-date-total' },
    { group:'mgr-proj-group', header:'mgr-proj-header', headerLeft:'mgr-proj-header-left', label:'mgr-proj-label', total:'mgr-proj-total' },
    { group:'mgr-phase-group', header:'mgr-phase-header', headerLeft:'mgr-phase-header-left', label:'mgr-phase-label', total:'mgr-phase-total' },
    { group:'mgr-act-group', header:'mgr-act-header', headerLeft:'mgr-act-header-left', label:'mgr-act-label', total:'mgr-act-total' },
  ];

  function buildStatusBadges(recs){
    var frag=document.createDocumentFragment();
    var submitted=recs.filter(function(r){return getStatus(r)==='Submitted';}).length;
    var postFailed=recs.filter(function(r){return getStatus(r)==='Post Failed';}).length;
    if(submitted>0){
      var b=document.createElement('span'); b.className='mgr-pending-badge';
      b.style.background='var(--amber-soft)'; b.style.color='var(--amber)'; b.style.borderColor='#f6cc6a'; b.style.fontWeight='600';
      b.textContent=submitted+' to review';
      frag.appendChild(b);
    }
    if(postFailed>0){
      var b2=document.createElement('span'); b2.className='mgr-pending-badge';
      b2.style.background='var(--red-soft)'; b2.style.color='#c0392b'; b2.style.borderColor='#e8a89e'; b2.style.fontWeight='600';
      b2.textContent=postFailed+' post failed';
      frag.appendChild(b2);
    }
    return frag;
  }

  // Builds one collapsible group header + body for a grouping level (project or activity code).
  // `key` must be unique across the whole render (used for collapsedGroups + auto-expand state).
  // Returns { group, body } — caller appends either entries or a deeper level's `group` into `body`.
  function buildLevelGroup(recs, key, label, cls){
    var total=recs.reduce(function(s,r){return s+getHours(r);},0);
    var bil=recs.filter(isBillable).reduce(function(s,r){return s+getHours(r);},0);
    var pct=total>0?Math.round(bil/total*100):null;
    // Charge Amount is already 0 on a non-billable entry (see phaseImpactFromRecord's precedence
    // note), so summing it directly IS the billable-dollar rollup — no isBillable filter needed.
    var fee=recs.reduce(function(s,r){return s+(parseFloat(r.fields['Charge Amount'])||0);},0);
    var g=document.createElement('div'); g.className=cls.group;
    var h=document.createElement('div'); h.className=cls.header;
    var hl=document.createElement('div'); hl.className=cls.headerLeft;
    var ids=recs.map(function(r){return r.id;});
    var allChecked=ids.every(function(id){return selectedIds[id];});
    var cb=document.createElement('input'); cb.type='checkbox'; cb.className='mgr-cb'; cb.checked=allChecked;
    cb.addEventListener('change',function(){
      ids.forEach(function(id){ selectedIds[id]=cb.checked; });
      renderBatchBar();
      // Every box below this one (nested group boxes + entry boxes) follows it.
      var cbs=g.querySelectorAll('input.mgr-cb');
      for(var ci=0;ci<cbs.length;ci++){ cbs[ci].checked=cb.checked; }
    });
    var lbl=document.createElement('span'); lbl.className=cls.label; lbl.textContent=label;
    var icon=document.createElement('span'); icon.className='mgr-collapse-icon'; icon.textContent='▼';
    hl.appendChild(cb); hl.appendChild(lbl); hl.appendChild(icon);
    hl.appendChild(buildStatusBadges(recs));
    var tot=document.createElement('span'); tot.className=cls.total;
    tot.textContent=total.toFixed(2)+' hrs'+(pct!==null?' · '+pct+'% bil':'')+' · '+phFmtCurrency(fee);
    h.appendChild(hl); h.appendChild(tot); g.appendChild(h);
    var body=document.createElement('div'); body.className='mgr-collapsible';
    if(collapsedGroups[key]===undefined && autoExpand) collapsedGroups[key]=false;   // gate launch → expanded
    if(collapsedGroups[key]!==false) { body.classList.add('collapsed'); h.classList.add('collapsed'); collapsedGroups[key]=true; }
    h.addEventListener('click',function(e){
      if(e.target.tagName==='INPUT') return;
      e.stopPropagation();
      collapsedGroups[key]=!collapsedGroups[key];
      body.classList.toggle('collapsed');
      h.classList.toggle('collapsed');
    });
    g.appendChild(body);
    return { group:g, body:body };
  }

  function filteredRecords() {
    return allRecords.filter(function(r){
      if(getStatus(r)==='Deleted') return false;
      if(!inReviewScope(getSubmitterId(r))) return false;   // only people assigned to this reviewer
      if(filterMembers.length>0 && filterMembers.indexOf(getSubmitterId(r))===-1) return false;
      if(filterStatuses.length>0 && filterStatuses.indexOf(getStatus(r))===-1) return false;
      return true;
    });
  }

  function selectedCount() { return Object.keys(selectedIds).filter(function(k){return selectedIds[k];}).length; }
  function selectedList()  { return Object.keys(selectedIds).filter(function(k){return selectedIds[k];}); }

  function updateLocal(rid,fields) {
    var sources=[allRecords,needsAttentionRecords];
    sources.forEach(function(src){
      var rec=src.find(function(r){return r.id===rid;}); if(!rec) return;
      Object.keys(fields).forEach(function(k){
        if(k==='Status') rec.fields['Status']={label:fields[k]};
        else if(k==='Activity Code'){ var m=ACTIVITY_CODES.find(function(a){return a.id===fields[k];}); if(m) rec.fields['Activity Code String']=m.label; }
        else if(k==='Phase Airtable ID') rec.fields['Phase Airtable ID']=fields[k]?[fields[k]]:[];
        else rec.fields[k]=fields[k];
      });
    });
  }

  // ── Multiselect widget ──
  var openMS = null;
  function buildMS(id, options, selected, placeholder, onChange) {
    var wrap = document.createElement('div'); wrap.className='mgr-ms-wrap'; wrap.id=id;
    var trigger = document.createElement('div'); trigger.className='mgr-ms-trigger';
    var arrow = document.createElement('span'); arrow.className='mgr-ms-arrow'; arrow.textContent='▼';
    trigger.appendChild(arrow);
    var dropdown = document.createElement('div'); dropdown.className='mgr-ms-dropdown';
    var search = document.createElement('input'); search.className='mgr-ms-search'; search.placeholder='Search...'; search.type='text';
    dropdown.appendChild(search);
    var optList = document.createElement('div'); optList.className='mgr-ms-options';
    dropdown.appendChild(optList);
    var actBar = document.createElement('div'); actBar.className='mgr-ms-actions';
    var allBtn = document.createElement('button'); allBtn.className='mgr-ms-action-btn'; allBtn.textContent='All';
    var noneBtn = document.createElement('button'); noneBtn.className='mgr-ms-action-btn'; noneBtn.textContent='None';
    actBar.appendChild(allBtn); actBar.appendChild(noneBtn); dropdown.appendChild(actBar);
    var current = selected.slice();
    var updateTrigger=function() {
      while(trigger.firstChild && trigger.firstChild!==arrow) trigger.removeChild(trigger.firstChild);
      var txt = document.createTextNode(current.length===0?placeholder:(current.length===options.length?'All':current.length+' selected'));
      trigger.insertBefore(txt, arrow);
    }
    var buildOptions=function(q) {
      while(optList.firstChild) optList.removeChild(optList.firstChild);
      options.filter(function(o){ return !q||o.label.toLowerCase().indexOf(q.toLowerCase())!==-1; }).forEach(function(o){
        var row=document.createElement('div'); row.className='mgr-ms-option';
        var cb=document.createElement('input'); cb.type='checkbox'; cb.className='mgr-cb'; cb.checked=current.indexOf(o.value)!==-1;
        var lbl=document.createElement('span'); lbl.textContent=o.label;
        cb.addEventListener('change',function(){
          if(cb.checked){ if(current.indexOf(o.value)===-1) current.push(o.value); }
          else { current=current.filter(function(v){return v!==o.value;}); }
          updateTrigger(); onChange(current.slice());
        });
        row.appendChild(cb); row.appendChild(lbl); optList.appendChild(row);
      });
    }
    search.addEventListener('input',function(){ buildOptions(search.value); });
    allBtn.addEventListener('click',function(){ current=options.map(function(o){return o.value;}); updateTrigger(); buildOptions(search.value); onChange(current.slice()); });
    noneBtn.addEventListener('click',function(){ current=[]; updateTrigger(); buildOptions(search.value); onChange(current.slice()); });
    trigger.addEventListener('click',function(e){
      e.stopPropagation();
      if(openMS&&openMS!==wrap) openMS.classList.remove('open');
      wrap.classList.toggle('open');
      openMS=wrap.classList.contains('open')?wrap:null;
      if(wrap.classList.contains('open')){ buildOptions(''); setTimeout(function(){search.focus();},50); }
    });
    buildOptions(''); updateTrigger();
    wrap.appendChild(trigger); wrap.appendChild(dropdown);
    return wrap;
  }

  function onDocClickCloseMS(e){
    if(openMS&&!openMS.contains(e.target)){ openMS.classList.remove('open'); openMS=null; }
  }
  document.addEventListener('click', onDocClickCloseMS);

  // ── Render ──
  // Emptying and rebuilding the list collapses the page for a moment, which clamps the scroll
  // position; every redraw puts it back exactly where the reviewer had it.
  function render() {
    var scroller = container.closest ? container.closest('.tr-modal-scroll') : null;
    var keepTop = scroller ? scroller.scrollTop : 0;
    renderList();
    if (scroller && Math.abs(scroller.scrollTop - keepTop) > 1) scroller.scrollTop = keepTop;
  }
  function renderList() {
    while(container.firstChild) container.removeChild(container.firstChild);
    var recs=filteredRecords();

    var fetchBar=document.createElement('div');
    fetchBar.className='mgr-fetch-bar'+(lastFetchError?' error':'');
    fetchBar.style.cssText='display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;';
    var fetchStatus=document.createElement('span'); fetchStatus.style.cssText='display:flex;align-items:center;gap:6px;';
    if(fetchingNow){
      var sp=document.createElement('span'); sp.className='mgr-fetch-spinner';
      fetchStatus.appendChild(sp);
      fetchStatus.appendChild(document.createTextNode('Refreshing...'));
    } else if(lastFetchError){
      fetchStatus.textContent='⚠ '+lastFetchError;
    } else if(lastFetchTime){
      fetchStatus.appendChild(document.createTextNode(allRecords.length+' records · updated '+lastFetchTime.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})));
    }
    fetchBar.appendChild(fetchStatus);

    // Billed Fee ↔ Core sync — lets a reviewer blocked here by the time gate confirm the EBR is
    // current before approving, without leaving Time Review to find the Admin tool.
    var syncWrap=document.createElement('span'); syncWrap.style.cssText='display:flex;align-items:center;gap:8px;';
    var syncBtn=document.createElement('button'); syncBtn.type='button';
    syncBtn.textContent=billedFeeSyncing?'Syncing…':'Sync Billed Fee';
    syncBtn.title='Re-pull BQE Core\'s billed-to-date and correct Billed Fee (and EBR) on every phase — same job that runs nightly.';
    syncBtn.disabled=billedFeeSyncing;
    syncBtn.style.cssText='font-family:"DM Sans",sans-serif;font-size:11px;font-weight:600;padding:4px 10px;border-radius:5px;background:none;border:1px solid var(--border);color:inherit;cursor:'+(billedFeeSyncing?'default':'pointer')+';opacity:'+(billedFeeSyncing?'0.6':'1')+';';
    syncBtn.addEventListener('click', syncBilledFeeNow);
    syncWrap.appendChild(syncBtn);
    var syncAgoEl=document.createElement('span');
    syncAgoEl.style.cssText='font-size:11px;font-family:"DM Mono",monospace;color:var(--text-soft);';
    syncAgoEl.title=billedFeeLastRunAt?new Date(billedFeeLastRunAt).toLocaleString('en-US'):'This sync has never run';
    syncAgoEl.textContent='Last synced '+fmtAgo(billedFeeLastRunAt);
    syncWrap.appendChild(syncAgoEl);
    if(billedFeeSyncMsg){
      var syncMsgEl=document.createElement('span');
      syncMsgEl.style.cssText='font-size:11px;font-family:"DM Mono",monospace;color:'+(billedFeeSyncMsg.ok?'var(--green)':'var(--red)')+';';
      syncMsgEl.textContent=billedFeeSyncMsg.text;
      syncWrap.appendChild(syncMsgEl);
    }
    fetchBar.appendChild(syncWrap);

    container.appendChild(fetchBar);
    var expBlock=buildExpensesBlock(); if(expBlock) container.appendChild(expBlock);
    var weekRecs=allRecords.filter(function(r){return getStatus(r)!=='Deleted';});

    // ── Summary ──
    var summary=document.createElement('div'); summary.className='mgr-summary';
    var sumHdr=document.createElement('div'); sumHdr.className='mgr-summary-header';
    var sumTitleWrap=document.createElement('div'); sumTitleWrap.style.cssText='display:flex;align-items:center;gap:5px;';
    var sumTitle=document.createElement('span'); sumTitle.className='mgr-summary-title'; sumTitle.textContent='Team Overview';
    var sumIcon=document.createElement('span'); sumIcon.className='mgr-collapse-icon'; sumIcon.textContent='▼';
    if(summaryCollapsed) sumIcon.style.transform='rotate(-90deg)';
    sumTitleWrap.appendChild(sumTitle); sumTitleWrap.appendChild(sumIcon);
    sumHdr.appendChild(sumTitleWrap); summary.appendChild(sumHdr);
    sumHdr.style.cursor='pointer';
    var sumBody=document.createElement('div');
    if(summaryCollapsed) sumBody.style.display='none';
    sumHdr.addEventListener('click',function(e){
      if(e.target.tagName==='BUTTON') return;
      summaryCollapsed=!summaryCollapsed;
      sumBody.style.display=summaryCollapsed?'none':'';
      sumIcon.style.transform=summaryCollapsed?'rotate(-90deg)':'';
    });

    var tbl=document.createElement('table'); tbl.className='mgr-summary-table';
    var thead=document.createElement('thead'); var hr=document.createElement('tr');
    ['Staff Member','Week Total','Billable','Overhead','% Billable','Charge Amt','Unsubmitted'].forEach(function(h){
      var th=document.createElement('th'); th.textContent=h; hr.appendChild(th);
    });
    thead.appendChild(hr); tbl.appendChild(thead);
    var tbody=document.createElement('tbody');
    allMembers.slice().filter(function(m){return inReviewScope(m.id);}).sort(function(a,b){
      return (a.fields['Member Name']||'').localeCompare(b.fields['Member Name']||'');
    }).forEach(function(m){
      var mid=m.id;
      var mRecs=weekRecs.filter(function(r){return getSubmitterId(r)===mid;});
      var total=mRecs.reduce(function(s,r){return s+getHours(r);},0);
      var billable=mRecs.filter(isBillable).reduce(function(s,r){return s+getHours(r);},0);
      var overhead=total-billable;
      var pctBil=total>0?Math.round(billable/total*100):null;
      var unsub=mRecs.filter(function(r){var st=getStatus(r);return st==='Pending Review'||st==='Rejected';}).reduce(function(s,r){return s+getHours(r);},0);
      var chargeAmt=mRecs.reduce(function(s,r){return s+(parseFloat(r.fields['Charge Amount'])||0);},0);
      var tr=document.createElement('tr');
      var td=function(txt,cls){ var t=document.createElement('td'); t.textContent=txt; if(cls) t.className=cls; return t; }
      tr.appendChild(td(m.fields['Member Name']||''));
      tr.appendChild(td(total.toFixed(2)+' h','col-total'));
      tr.appendChild(td(billable.toFixed(2)+' h'));
      tr.appendChild(td(overhead.toFixed(2)+' h'));
      tr.appendChild(td(pctBil!==null?pctBil+'%':'—', pctBil!==null?(pctBil>=70?'col-ok':pctBil<50?'col-warn':''):'' ));
      tr.appendChild(td(chargeAmt>0?'$'+chargeAmt.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'—'));
      var unsubTd=td(unsub>0?unsub.toFixed(2)+' h':'—',unsub>0?'col-warn':''); tr.appendChild(unsubTd);
      tbody.appendChild(tr);
    });
    var tfoot=document.createElement('tfoot');
    var ttr=document.createElement('tr'); ttr.style.borderTop='2px solid var(--aj-border-mid)';
    var ttd=function(txt,cls){
      var t=document.createElement('td'); t.textContent=txt;
      t.style.padding='8px 10px'; t.style.fontFamily='DM Mono,monospace';
      t.style.fontSize='12px'; t.style.textAlign='right';
      t.style.fontWeight='700'; t.style.color='var(--aj-text)';
      if(cls) t.className=cls; return t;
    }
    var teamTotal=0, teamBil=0, teamOverhead=0, teamCharge=0, teamUnsub=0;
    allMembers.filter(function(m){return inReviewScope(m.id);}).forEach(function(m){
      var mRecs=weekRecs.filter(function(r){return getSubmitterId(r)===m.id;});
      teamTotal+=mRecs.reduce(function(s,r){return s+getHours(r);},0);
      teamBil+=mRecs.filter(isBillable).reduce(function(s,r){return s+getHours(r);},0);
      teamOverhead+=mRecs.filter(function(r){return !isBillable(r);}).reduce(function(s,r){return s+getHours(r);},0);
      teamCharge+=mRecs.reduce(function(s,r){return s+(parseFloat(r.fields['Charge Amount'])||0);},0);
      teamUnsub+=mRecs.filter(function(r){var st=getStatus(r);return st==='Pending Review'||st==='Rejected';}).reduce(function(s,r){return s+getHours(r);},0);
    });
    var teamPct=teamTotal>0?Math.round(teamBil/teamTotal*100):null;
    var tlbl=document.createElement('td'); tlbl.textContent='Team';
    tlbl.style.padding='8px 10px'; tlbl.style.fontSize='12px'; tlbl.style.fontWeight='700'; tlbl.style.color='var(--aj-text)';
    ttr.appendChild(tlbl);
    ttr.appendChild(ttd(teamTotal.toFixed(2)+' h','col-total'));
    ttr.appendChild(ttd(teamBil.toFixed(2)+' h'));
    ttr.appendChild(ttd(teamOverhead.toFixed(2)+' h'));
    ttr.appendChild(ttd(teamPct!==null?teamPct+'%':'—', teamPct!==null?(teamPct>=70?'col-ok':teamPct<50?'col-warn':''):'' ));
    ttr.appendChild(ttd(teamCharge>0?'$'+teamCharge.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'—'));
    ttr.appendChild(ttd(teamUnsub>0?teamUnsub.toFixed(2)+' h':'—',teamUnsub>0?'col-warn':''));
    tfoot.appendChild(ttr); tbl.appendChild(tfoot);
    tbl.appendChild(tbody);
    var sumWrap=document.createElement('div'); sumWrap.className='mgr-summary-wrap';
    sumWrap.appendChild(tbl); sumBody.appendChild(sumWrap);
    summary.appendChild(sumBody);

    // ── Filters ──
    var filters=document.createElement('div'); filters.className='mgr-filters';
    var fRow=document.createElement('div'); fRow.className='mgr-filters-row';

    var fg1=document.createElement('div'); fg1.className='mgr-filter-group';
    var fl1=document.createElement('span'); fl1.className='mgr-filter-label'; fl1.textContent='Staff Member';
    var memberOptions=allMembers.filter(function(m){return inReviewScope(m.id);}).map(function(m){return{value:m.id,label:m.fields['Member Name']||''};});
    var msMembers=buildMS('ms-members',memberOptions,filterMembers,'All Staff',function(sel){ filterMembers=sel; });
    fg1.appendChild(fl1); fg1.appendChild(msMembers);

    var fg2=document.createElement('div'); fg2.className='mgr-filter-group';
    var fl2=document.createElement('span'); fl2.className='mgr-filter-label'; fl2.textContent='Status';
    var statusOptions=['Pending Review','Submitted','Approved','Rejected','Post Failed','Posted'].map(function(s){return{value:s,label:s};});
    var msStatus=buildMS('ms-status',statusOptions,filterStatuses,'All Statuses',function(sel){ filterStatuses=sel; });
    fg2.appendChild(fl2); fg2.appendChild(msStatus);

    var fg3=document.createElement('div'); fg3.className='mgr-filter-group';
    var fl3=document.createElement('span'); fl3.className='mgr-filter-label'; fl3.textContent='From';
    var inp3=document.createElement('input'); inp3.type='date'; inp3.className='mgr-filter-input'; inp3.id='mgr-f-from'; inp3.value=filterDateFrom;
    fg3.appendChild(fl3); fg3.appendChild(inp3);

    var fg4=document.createElement('div'); fg4.className='mgr-filter-group';
    var fl4=document.createElement('span'); fl4.className='mgr-filter-label'; fl4.textContent='To';
    var inp4=document.createElement('input'); inp4.type='date'; inp4.className='mgr-filter-input'; inp4.id='mgr-f-to'; inp4.value=filterDateTo;
    fg4.appendChild(fl4); fg4.appendChild(inp4);

    var prevWkBtn=document.createElement('button'); prevWkBtn.className='mgr-week-nav-btn'; prevWkBtn.textContent='‹';
    prevWkBtn.title='Previous week';
    prevWkBtn.addEventListener('click',function(){
      var mon=getMonday(new Date(filterDateFrom+'T00:00:00'));
      mon.setDate(mon.getDate()-7);
      filterDateFrom=fmtDate(mon); filterDateTo=fmtDate(addDays(mon,6));
      selectedIds={}; editingId=null; rejectingId=null; batchRejectOpen=false;
      loadAll();
    });
    var nextWkBtn=document.createElement('button'); nextWkBtn.className='mgr-week-nav-btn'; nextWkBtn.textContent='›';
    nextWkBtn.title='Next week';
    nextWkBtn.addEventListener('click',function(){
      var mon=getMonday(new Date(filterDateFrom+'T00:00:00'));
      mon.setDate(mon.getDate()+7);
      filterDateFrom=fmtDate(mon); filterDateTo=fmtDate(addDays(mon,6));
      selectedIds={}; editingId=null; rejectingId=null; batchRejectOpen=false;
      loadAll();
    });

    var applyBtn=document.createElement('button'); applyBtn.className='mgr-filter-btn btn-apply'; applyBtn.textContent='Apply';
    applyBtn.addEventListener('click',function(){
      filterDateFrom=document.getElementById('mgr-f-from').value;
      filterDateTo=document.getElementById('mgr-f-to').value;
      selectedIds={}; editingId=null; rejectingId=null; batchRejectOpen=false;
      loadAll();
    });
    var resetBtn=document.createElement('button'); resetBtn.className='mgr-filter-btn btn-reset'; resetBtn.textContent='Reset';
    resetBtn.addEventListener('click',function(){
      filterMembers=[]; filterStatuses=[];
      var rMon=getMonday(new Date()); filterDateFrom=fmtDate(rMon); filterDateTo=fmtDate(addDays(rMon,6));
      weekOffset=0; selectedIds={}; editingId=null; rejectingId=null; batchRejectOpen=false;
      loadAll();
    });

    var dateRow=document.createElement('div'); dateRow.className='mgr-filter-date-row';
    dateRow.appendChild(prevWkBtn); dateRow.appendChild(fg3); dateRow.appendChild(fg4);
    dateRow.appendChild(nextWkBtn); dateRow.appendChild(applyBtn); dateRow.appendChild(resetBtn);

    fRow.appendChild(fg1); fRow.appendChild(fg2); fRow.appendChild(dateRow);
    filters.appendChild(fRow);

    // Badge reflects the ACTIVE filters (member / status) so it matches what's shown — e.g. when opened
    // from the time gate filtered to one member, the count is just that member's, not the firm total.
    function matchesActiveFilters(r){
      if(!inReviewScope(getSubmitterId(r))) return false;
      if(filterMembers.length>0 && filterMembers.indexOf(getSubmitterId(r))===-1) return false;
      if(filterStatuses.length>0 && filterStatuses.indexOf(getStatus(r))===-1) return false;
      return true;
    }
    // Week/Needs-Attention toggle removed — the time-gate lockout now surfaces anything outstanding
    // (review backlog + failed posts). This is one filterable view driven by the Staff/Status/date
    // controls above; deep-links (e.g. "Retry in Time Review") preset those filters + the date range.

    container.appendChild(filters);
    container.appendChild(summary);

    if(needsAttentionView){
      var attnDiv=document.createElement('div'); attnDiv.className='mgr-list';
      // Honor the active Staff Member / Status filters here too (e.g. opened from the time gate for one
      // member's submitted entries) — the Needs Attention list is date-independent so it isn't bounded
      // by the week the date range sits on.
      var attnRecs = needsAttentionRecords.filter(function(r){
        if(!inReviewScope(getSubmitterId(r))) return false;
        // Drop entries that were just posted/approved (optimistic) so they vanish from the list
        // immediately after a batch, before the authoritative reload lands. Post Failed stays.
        var st=getStatus(r); if(st==='Posted'||st==='Approved') return false;
        if(filterMembers.length>0 && filterMembers.indexOf(getSubmitterId(r))===-1) return false;
        if(filterStatuses.length>0 && filterStatuses.indexOf(getStatus(r))===-1) return false;
        return true;
      });
      if(needsAttentionFetching){
        var fetchMsg=document.createElement('div'); fetchMsg.className='mgr-empty'; fetchMsg.textContent='Loading…';
        attnDiv.appendChild(fetchMsg);
      } else if(attnRecs.length===0){
        var emptyAttn=document.createElement('div'); emptyAttn.className='mgr-attn-empty';
        emptyAttn.textContent=(filterMembers.length||filterStatuses.length)
          ? '✓ Nothing needs attention for the current filter.'
          : '✓ Nothing needs attention — all entries are reviewed or posted.';
        attnDiv.appendChild(emptyAttn);
      } else {
        var attnByMember={};
        attnRecs.forEach(function(r){
          var sid=getSubmitterId(r);
          if(!attnByMember[sid]) attnByMember[sid]=[];
          attnByMember[sid].push(r);
        });
        Object.keys(attnByMember).sort(function(a,b){
          var an=(allMembers.find(function(m){return m.id===a;})||{fields:{}}).fields['Member Name']||'';
          var bn=(allMembers.find(function(m){return m.id===b;})||{fields:{}}).fields['Member Name']||'';
          return an.localeCompare(bn);
        }).forEach(function(mid){
          var mRec=allMembers.find(function(m){return m.id===mid;});
          var mName=mRec?(mRec.fields['Member Name']||'Unknown'):'Unknown';
          var mRecs=attnByMember[mid];
          var pendingRecs=mRecs.filter(function(r){return (r.fields['Status']||'')==='Pending Review';});
          var submittedRecs=mRecs.filter(function(r){return (r.fields['Status']||'')==='Submitted';});
          var rejectedRecs=mRecs.filter(function(r){return (r.fields['Status']||'')==='Rejected';});
          var mHead=document.createElement('div'); mHead.className='mgr-attn-member';
          var mNameSpan=document.createElement('span'); mNameSpan.textContent=mName;
          var mCounts=document.createElement('div'); mCounts.className='mgr-attn-member-counts';
          if(pendingRecs.length){var pc=document.createElement('span');pc.className='mgr-attn-count pending';pc.textContent=pendingRecs.length+' unsubmitted';mCounts.appendChild(pc);}
          if(submittedRecs.length){var sc=document.createElement('span');sc.className='mgr-attn-count submitted';sc.textContent=submittedRecs.length+' awaiting approval';mCounts.appendChild(sc);}
          if(rejectedRecs.length){var rc=document.createElement('span');rc.className='mgr-attn-count rejected';rc.textContent=rejectedRecs.length+' rejected';mCounts.appendChild(rc);}
          mHead.appendChild(mNameSpan); mHead.appendChild(mCounts);
          attnDiv.appendChild(mHead);
          mRecs.sort(function(a,b){
            var da=a.fields['Entry Date']||'', db=b.fields['Entry Date']||'';
            return da<db?-1:da>db?1:0;
          });
          mRecs.forEach(function(r){ attnDiv.appendChild(buildEntry(r)); });
        });
      }
      container.appendChild(attnDiv);
      renderBatchBar();
      applyFocus();
      return;
    }

    var listDiv=document.createElement('div'); listDiv.className='mgr-list';
    if(recs.length===0){
      var empty=document.createElement('div'); empty.className='mgr-empty'; empty.textContent='No entries match the current filters.';
      listDiv.appendChild(empty);
    } else {
      // Day → Project → Person → Phase → Activity Code. Day is the heaviest header (reuses the
      // original person-level chrome: name/date line + Total/Billable/%Bil/Unsubmitted stats +
      // pending badge) since it's now the top tier; Project/Person/Phase/Activity are generic
      // collapsible levels via buildLevelGroup(), each using LEVEL_TIERS by nesting depth so a
      // little lighter/more indented than the one above.
      var byDay={};
      recs.forEach(function(r){ var ds=getEntryDate(r); if(!byDay[ds]) byDay[ds]=[]; byDay[ds].push(r); });
      Object.keys(byDay).sort().forEach(function(ds){
        var dRecs=byDay[ds];
        var dTotal=dRecs.reduce(function(s,r){return s+getHours(r);},0);
        var dBil=dRecs.filter(isBillable).reduce(function(s,r){return s+getHours(r);},0);
        var dPct=dTotal>0?Math.round(dBil/dTotal*100):null;
        var dUnsub=dRecs.filter(function(r){var st=getStatus(r);return st==='Pending Review'||st==='Rejected';}).reduce(function(s,r){return s+getHours(r);},0);
        var dFee=dRecs.reduce(function(s,r){return s+(parseFloat(r.fields['Charge Amount'])||0);},0);
        var dg=document.createElement('div'); dg.className='mgr-person-group';
        var dh=document.createElement('div'); dh.className='mgr-person-header';
        var dhl=document.createElement('div'); dhl.className='mgr-person-header-left';
        var dName=document.createElement('span'); dName.className='mgr-person-name'; dName.textContent=fmtDisplay(ds);
        var dIcon=document.createElement('span'); dIcon.className='mgr-collapse-icon'; dIcon.textContent='▼';
        // Select the whole day in one click (the project groups below were the widest bulk
        // selection before). Same contract as buildLevelGroup's box: header clicks on an INPUT
        // don't collapse, and every nested box follows it.
        var dIds=dRecs.map(function(r){return r.id;});
        var dCb=document.createElement('input'); dCb.type='checkbox'; dCb.className='mgr-cb'; dCb.title='Select every entry on this day';
        dCb.checked=dIds.length>0 && dIds.every(function(id){return selectedIds[id];});
        dCb.addEventListener('change',function(){
          dIds.forEach(function(id){ selectedIds[id]=dCb.checked; });
          renderBatchBar();
          var cbs=dg.querySelectorAll('input.mgr-cb');
          for(var ci=0;ci<cbs.length;ci++){ cbs[ci].checked=dCb.checked; }
        });
        var dPendingCount=dRecs.filter(function(r){var st=getStatus(r);return st==='Submitted'||st==='Post Failed';}).length;
        dhl.appendChild(dCb); dhl.appendChild(dName); dhl.appendChild(dIcon);
        if(dPendingCount>0){
          var dBadge=document.createElement('span'); dBadge.className='mgr-pending-badge';
          dBadge.textContent=dPendingCount+' pending';
          dhl.appendChild(dBadge);
        }
        var dStats=document.createElement('div'); dStats.className='mgr-person-stats';
        var dstat=function(label,val,warn){ var s=document.createElement('span'); s.className='mgr-person-stat'; if(warn) s.style.color='var(--amber)'; var lt=document.createTextNode(label+': '); var st=document.createElement('strong'); st.textContent=val; s.appendChild(lt); s.appendChild(st); return s; }
        dStats.appendChild(dstat('Total',dTotal.toFixed(2)+' h'));
        dStats.appendChild(dstat('Billable',dBil.toFixed(2)+' h'));
        if(dPct!==null) dStats.appendChild(dstat('%Bil',dPct+'%'));
        dStats.appendChild(dstat('Billable $',phFmtCurrency(dFee)));
        if(dUnsub>0) dStats.appendChild(dstat('Unsubmitted',dUnsub.toFixed(2)+' h',true));
        dh.appendChild(dhl); dh.appendChild(dStats); dg.appendChild(dh);
        var cds=cortexDaySummary(ds, dRecs);
        if(cds){ var cdl=document.createElement('div'); cdl.style.cssText='font-size:11px;color:var(--aj-text-muted);padding:2px 12px 6px;'; cdl.textContent=cds; dg.appendChild(cdl); }
        var dgBody=document.createElement('div'); dgBody.className='mgr-collapsible';
        if(collapsedDays[ds]) { dgBody.classList.add('collapsed'); dh.classList.add('collapsed'); }
        dh.addEventListener('click',function(e){
          if(e.target.tagName==='INPUT') return;
          e.stopPropagation();
          collapsedDays[ds]=!collapsedDays[ds];
          dgBody.classList.toggle('collapsed');
          dh.classList.toggle('collapsed');
        });

        var byProject={};
        dRecs.forEach(function(r){ var pn=getProjectName(r); if(!byProject[pn]) byProject[pn]=[]; byProject[pn].push(r); });
        Object.keys(byProject).sort(function(a,b){return a.localeCompare(b);}).forEach(function(pn){
          var pRecs=byProject[pn];
          var pLvl=buildLevelGroup(pRecs, ds+'_p_'+pn, pn, LEVEL_TIERS[0]);
          var byMember={};
          pRecs.forEach(function(r){ var sid=getSubmitterId(r); if(!byMember[sid]) byMember[sid]=[]; byMember[sid].push(r); });
          Object.keys(byMember).sort(function(a,b){
            var an=(allMembers.find(function(m){return m.id===a;})||{fields:{}}).fields['Member Name']||'';
            var bn=(allMembers.find(function(m){return m.id===b;})||{fields:{}}).fields['Member Name']||'';
            return an.localeCompare(bn);
          }).forEach(function(mid){
            var mRec=allMembers.find(function(m){return m.id===mid;});
            var mName=mRec?mRec.fields['Member Name']||'Unknown':'Unknown';
            var mRecs=byMember[mid];
            var mLvl=buildLevelGroup(mRecs, ds+'_p_'+pn+'_m_'+mid, mName, LEVEL_TIERS[1]);
            var byPhase={};
            mRecs.forEach(function(r){ var phn=getPhaseName(r); if(!byPhase[phn]) byPhase[phn]=[]; byPhase[phn].push(r); });
            Object.keys(byPhase).sort(function(a,b){return a.localeCompare(b);}).forEach(function(phn){
              var phRecs=byPhase[phn];
              var phLvl=buildLevelGroup(phRecs, ds+'_p_'+pn+'_m_'+mid+'_ph_'+phn, phn, LEVEL_TIERS[2]);
              var byActivity={};
              phRecs.forEach(function(r){ var ac=getActivityCode(r); if(!byActivity[ac]) byActivity[ac]=[]; byActivity[ac].push(r); });
              Object.keys(byActivity).sort(function(a,b){return a.localeCompare(b);}).forEach(function(ac){
                var aRecs=byActivity[ac];
                var aLvl=buildLevelGroup(aRecs, ds+'_p_'+pn+'_m_'+mid+'_ph_'+phn+'_a_'+ac, ac, LEVEL_TIERS[3]);
                aRecs.forEach(function(r){ aLvl.body.appendChild(buildEntry(r)); });
                phLvl.body.appendChild(aLvl.group);
              });
              mLvl.body.appendChild(phLvl.group);
            });
            pLvl.body.appendChild(mLvl.group);
          });
          dgBody.appendChild(pLvl.group);
        });
        dg.appendChild(dgBody);
        listDiv.appendChild(dg);
      });
    }
    container.appendChild(listDiv);
    renderBatchBar();
    applyFocus();
  }

  function renderBatchBar() {
    var existing=container.querySelector('#mgr-batch-bar');
    if(existing) existing.parentNode.removeChild(existing);
    var sel=selectedList();
    if(sel.length===0) return;
    var bar=document.createElement('div'); bar.className='mgr-batch-bar'; bar.id='mgr-batch-bar';
    var activeRecs=needsAttentionView?needsAttentionRecords:allRecords;
    var selHours=sel.reduce(function(s,rid){
      var rec=activeRecs.find(function(r){return r.id===rid;});
      return s+(rec?getHours(rec):0);
    },0);
    var count=document.createElement('span'); count.className='mgr-batch-count';
    count.textContent=sel.length+' entr'+(sel.length===1?'y':'ies')+' selected · '+selHours.toFixed(2)+' hrs';
    bar.appendChild(count);

    if(!batchRejectOpen) {
      var appBtn=document.createElement('button'); appBtn.className='mgr-batch-btn batch-approve'; appBtn.textContent='Approve All';
      appBtn.addEventListener('click',function(){
        appBtn.disabled=true; appBtn.textContent='Starting…';
        // Entries whose activity code doesn't fit their project are left out (Core would refuse them).
        var toPost=sel.filter(function(rid){
          var rec=(needsAttentionView?needsAttentionRecords:allRecords).find(function(x){ return x.id===rid; });
          return !(rec && tc.activityCodeProblem(rec.fields['Phase Name'], rec.fields['Activity Code String']));
        });
        var skippedCode=sel.length-toPost.length;
        if(skippedCode) alertDialog({ title: skippedCode+' entr'+(skippedCode===1?'y':'ies')+' left out', message: 'The activity code does not fit the project (Alder James Overhead takes OP- or PTO- codes; every other project takes AS- codes). Edit '+(skippedCode===1?'it':'them')+' and approve again.' });
        var getLabelForRid = function(rid) {
          var rec=(needsAttentionView?needsAttentionRecords:allRecords).find(function(r){ return r.id===rid; });
          if(!rec) return rid;
          var f=rec.fields;
          var member=f['Member Name']||'';
          var date=(f['Entry Date']||'').split('T')[0].slice(5);
          var hours=parseFloat(f['Actual Hours']||0).toFixed(1);
          var phase=Array.isArray(f['Phase Name'])?f['Phase Name'][0]:(f['Phase Name']||'');
          var phaseShort=phase.length>22?phase.substring(0,20)+'…':phase;
          return member+' · '+date+' · '+hours+'h'+(phaseShort?' · '+phaseShort:'');
        }
        bar.innerHTML='';
        var titleRow=document.createElement('div');
        titleRow.style.cssText='width:100%;display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;';
        var titleTxt=document.createElement('span');
        titleTxt.style.cssText='font-size:13px;font-weight:600;';
        titleTxt.textContent='Posting '+toPost.length+' entr'+(toPost.length===1?'y':'ies')+' to BQE Core…';
        titleRow.appendChild(titleTxt);
        bar.appendChild(titleRow);
        var progressList=document.createElement('div'); progressList.className='batch-progress-list';
        var progressRows={};
        toPost.forEach(function(rid){
          var row=document.createElement('div'); row.className='batch-progress-row';
          var icon=document.createElement('span'); icon.className='batch-progress-icon'; icon.textContent='·';
          var label=document.createElement('span'); label.className='batch-progress-label'; label.textContent=getLabelForRid(rid);
          var state=document.createElement('span'); state.className='batch-progress-state pending'; state.textContent='Waiting';
          row.appendChild(icon); row.appendChild(label); row.appendChild(state);
          progressList.appendChild(row);
          progressRows[rid]={icon:icon,state:state,row:row};
        });
        bar.appendChild(progressList);
        var successCount=0; var failCount=0;
        var postNext=function(index) {
          if(index>=toPost.length){
            titleTxt.textContent=(failCount===0?'✓ All posted to BQE Core':successCount+' posted, '+failCount+' failed');
            titleTxt.style.color=failCount===0?'#5dd49a':'#f5a623';
            var summaryRow=document.createElement('div'); summaryRow.className='batch-progress-summary';
            if(failCount===0){
              setTimeout(function(){
                selectedIds={}; batchRejectOpen=false;
                lastActionTime=0;            // drop the action-grace so the refresh isn't held back
                renderBatchBar();
                render();                    // optimistic: posted entries already hidden
                if(needsAttentionView) loadNeedsAttention(); else loadAll();  // authoritative refresh now
              },900);
            } else {
              var doneBtn=document.createElement('button'); doneBtn.className='batch-done-btn'; doneBtn.textContent='Done';
              doneBtn.addEventListener('click',function(){
                selectedIds={}; batchRejectOpen=false;
                lastActionTime=0;
                renderBatchBar();
                if(needsAttentionView) loadNeedsAttention(); else loadAll();
              });
              summaryRow.appendChild(doneBtn);
              bar.appendChild(summaryRow);
            }
            return;
          }
          var rid=toPost[index];
          var pr=progressRows[rid];
          pr.icon.textContent='';
          var spinEl=document.createElement('span'); spinEl.className='bqe-post-spinner';
          spinEl.style.cssText='display:inline-block;width:9px;height:9px;border:1.5px solid rgba(255,255,255,0.2);border-top-color:#7ec8f0;border-radius:50%;animation:mgr-spin2 0.8s linear infinite;vertical-align:middle;';
          pr.icon.appendChild(spinEl);
          pr.state.className='batch-progress-state posting'; pr.state.textContent='Posting…';
          try { pr.row.scrollIntoView({ block:'nearest' }); } catch(e) {}
          var batchImpact=phaseImpactFromRecord(activeRecs.find(function(x){return x.id===rid;}));
          atPatch(TBL_STAGING,rid,{'Status':'Approved'}).then(function(){
            return tc.postTimeEntryToCore(rid);
          }).then(function(result){
            while(pr.icon.firstChild) pr.icon.removeChild(pr.icon.firstChild);
            if(result&&result.success){
              pr.icon.textContent='✓';
              pr.state.className='batch-progress-state done'; pr.state.textContent='Posted';
              updateLocal(rid,{'Status':'Posted'});
              // Same immediate local patch as the single-entry Approve — repaint every OTHER
              // pending entry against this phase as each one in the batch clears, not just at
              // the very end when loadAll() eventually re-fetches.
              patchPhaseAfterPost(batchImpact.phId, batchImpact.hours, batchImpact.fee);
              refreshPhaseScheduleBars(batchImpact.phId, rid);
              successCount++;
            } else {
              pr.icon.textContent='⚠';
              pr.state.className='batch-progress-state failed'; pr.state.textContent='Failed';
              updateLocal(rid,{'Status':'Post Failed'});
              failCount++;
            }
            postNext(index+1);
          }).catch(function(){
            while(pr.icon.firstChild) pr.icon.removeChild(pr.icon.firstChild);
            pr.icon.textContent='⚠';
            pr.state.className='batch-progress-state failed'; pr.state.textContent='Network error';
            updateLocal(rid,{'Status':'Post Failed'});
            failCount++;
            postNext(index+1);
          });
        }
        postNext(0);
      });
      var rejBtn=document.createElement('button'); rejBtn.className='mgr-batch-btn batch-reject'; rejBtn.textContent='Reject All';
      rejBtn.addEventListener('click',function(){ batchRejectOpen=true; renderBatchBar(); });
      var canBtn=document.createElement('button'); canBtn.className='mgr-batch-btn batch-cancel'; canBtn.textContent='Clear';
      canBtn.addEventListener('click',function(){ selectedIds={}; batchRejectOpen=false; renderBatchBar(); render(); });
      // Hold for onboarding, in bulk (Matt, 2026-10-19): one pick of the lead/opportunity holds every
      // selected SUBMITTED entry against it (database/308). Others in the selection are left alone.
      var holdBtn=document.createElement('button'); holdBtn.className='mgr-batch-btn batch-cancel'; holdBtn.textContent='Hold for onboarding';
      holdBtn.title='Hold the selected submitted entries for a project that is not set up yet (14 days)';
      holdBtn.addEventListener('click',function(){
        var subIds=sel.filter(function(rid){ var r=activeRecs.find(function(x){return x.id===rid;}); return r && getStatus(r)==='Submitted'; });
        if(!subIds.length){ alertDialog({title:'Nothing to hold',message:'Only submitted entries can be held. None of the selected entries are submitted.'}); return; }
        var hrs=subIds.reduce(function(t,rid){ var r=activeRecs.find(function(x){return x.id===rid;}); return t+(parseFloat(r&&r.fields['Actual Hours'])||0); },0);
        var names={}; subIds.forEach(function(rid){ var r=activeRecs.find(function(x){return x.id===rid;}); if(r) names[r.fields['Member Name']||'']=1; });
        var who=Object.keys(names).filter(Boolean);
        import('./HoldForOnboardingPicker.jsx').then(function(m){
          return m.openHoldPicker({ memberName: who.length===1 ? who[0] : (subIds.length+' entries from '+who.length+' people'), hours: Math.round(hrs*10)/10 });
        }).then(function(pick){
          if(!pick) return;
          holdBtn.disabled=true; holdBtn.textContent='Holding '+subIds.length+'…';
          var fails=0;
          return Promise.all(subIds.map(function(rid){
            return tc.holdEntry(rid,pick).then(function(d){
              if(d&&d.error){ fails++; return; }
              var row=d&&d.row||{};
              updateLocal(rid,{'Hold Until':row.hold_until||'','Hold Ref Name':row.hold_ref_name||pick.refName,'Hold Kind':row.hold_kind||pick.kind});
            }).catch(function(){ fails++; });
          })).then(function(){
            selectedIds={}; batchRejectOpen=false; lastActionTime=Date.now();
            renderBatchBar(); render();
            if(fails) alertDialog({title:'Some were not held',message:(subIds.length-fails)+' held for '+pick.refName+'; '+fails+' could not be held. Try those again.'});
          });
        });
      });
      bar.appendChild(appBtn); bar.appendChild(holdBtn); bar.appendChild(rejBtn); bar.appendChild(canBtn);
    } else {
      var rejRow=document.createElement('div'); rejRow.className='mgr-batch-reject-row';
      var noteInput=document.createElement('input'); noteInput.className='mgr-batch-note'; noteInput.placeholder='Reason for rejection (sent to all selected)...'; noteInput.type='text';
      var confBtn=document.createElement('button'); confBtn.className='mgr-batch-btn batch-confirm-reject'; confBtn.textContent='Confirm Reject All';
      confBtn.addEventListener('click',function(){
        var note=noteInput.value.trim();
        confBtn.textContent='Rejecting…'; confBtn.disabled=true;
        var toReject=sel.slice();
        toReject.forEach(function(rid){
          var entryRow=findByRid(rid);
          if(entryRow) entryRow.classList.add('is-rejecting');
        });
        var patches=toReject.map(function(rid){
          return atPatch(TBL_STAGING,rid,{'Status':'Rejected','Rejection Notes':note}).then(function(){
            tc.notifyRejection(rid, note).catch(function(){});
          }).catch(function(){});
        });
        Promise.all(patches).then(function(){
          toReject.forEach(function(rid){
            var entryRow=findByRid(rid);
            if(entryRow) entryRow.classList.add('approving-fade');
            updateLocal(rid,{'Status':'Rejected','Rejection Notes':note});
          });
          selectedIds={}; batchRejectOpen=false;
          lastActionTime=Date.now();
          setTimeout(function(){
            renderBatchBar();
            setTimeout(function(){ maybeReload(); },500);
          },400);
        });
      });
      var backBtn=document.createElement('button'); backBtn.className='mgr-batch-btn batch-cancel'; backBtn.textContent='Back';
      backBtn.addEventListener('click',function(){ batchRejectOpen=false; renderBatchBar(); });
      rejRow.appendChild(noteInput); rejRow.appendChild(confBtn); rejRow.appendChild(backBtn);
      bar.appendChild(rejRow);
    }
    container.appendChild(bar);
  }

  // ── Phase financial helpers ──
  function makeFinStat(label, val) {
    var s=document.createElement('span');
    s.style.fontSize='10px'; s.style.color='var(--text-soft)'; s.style.fontFamily='DM Mono,monospace'; s.style.whiteSpace='nowrap';
    var lbl=document.createElement('span');
    lbl.style.fontSize='10px'; lbl.style.color='var(--text-soft)'; lbl.style.textTransform='uppercase';
    lbl.style.letterSpacing='0.3px'; lbl.style.marginRight='3px'; lbl.style.fontFamily='DM Sans,sans-serif'; lbl.style.fontWeight='600';
    lbl.textContent=label; s.appendChild(lbl); s.appendChild(document.createTextNode(val)); return s;
  }
  function phFmtCurrency(v){ return v!=null&&isFinite(v)&&!isNaN(v)?'$'+Number(v).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0}):'—'; }
  function phFmtHours(v){ return v!=null&&isFinite(v)&&!isNaN(v)?Number(v).toFixed(1)+' hrs':'—'; }
  function phFmtRate(v){ return v!=null&&isFinite(v)&&!isNaN(v)&&v>0?'$'+Number(v).toFixed(0)+'/hr':'—'; }

  // Extracted out of buildEntry so it can be rebuilt on demand for entries OTHER than the one just
  // approved (see refreshPhaseBudgetBars) — the Contract/Billed/Fee-Exp/EBR chip row + the
  // over/near-budget alert, shown above the schedule line whenever the phase has any financial
  // data at all. Same staleness problem as buildScheduleBar below, same fix.
  function buildFinBar(r) {
    var f=r.fields;
    var project=Array.isArray(f['Project Name'])?f['Project Name'][0]:(f['Project Name']||'');
    var phRawId=f['Phase Airtable ID'];
    var phId=Array.isArray(phRawId)?(phRawId[0] && phRawId[0].id ? phRawId[0].id : phRawId[0]):(phRawId||'');
    var phData=allPhases.find(function(p){return p.id===phId;});
    if(!(phData&&(phData.hoursExpended||phData.feeExpended||phData.contractedFee||phData.billedFee)&&project.indexOf('Alder James Overhead')===-1)) return null;
    var finBar=document.createElement('div');
    finBar.setAttribute('data-finbar-rid', r.id);
    if(phId) finBar.setAttribute('data-finbar-phid', phId);
    finBar.style.cssText='display:flex;gap:12px;margin-top:3px;flex-wrap:wrap;width:100%;';
    var budgetAlert='';
    if(phData.contractType!=='Hourly'&&phData.contractedFee&&phData.feeExpended){
      var pctf=phData.feeExpended/phData.contractedFee;
      if(pctf>=1) budgetAlert='over';
      else if(pctf>=0.8) budgetAlert='near';
    }
    if(phData.contractedFee>0) finBar.appendChild(makeFinStat('Contract ',phFmtCurrency(phData.contractedFee)));
    if(phData.billedFee!=null) finBar.appendChild(makeFinStat('Billed ',phFmtCurrency(phData.billedFee)));
    if(phData.feeExpended!=null)   finBar.appendChild(makeFinStat('Fee Exp ',phFmtCurrency(phData.feeExpended)));
    if(phData.hoursExpended!=null) finBar.appendChild(makeFinStat('Exp ',phFmtHours(phData.hoursExpended)));
    if(phData.effectiveBillingRate!=null) finBar.appendChild(makeFinStat('EBR ',phFmtRate(phData.effectiveBillingRate)));
    if(budgetAlert){
      var alertEl=document.createElement('span');
      var ebrRatio2=phData.effectiveBillingRate&&targetEBR?phData.effectiveBillingRate/targetEBR:null;
      var ebrHigh2=ebrRatio2!=null&&ebrRatio2>1.2;
      var ebrRedHigh2=ebrRatio2!=null&&ebrRatio2>1.7;
      var ebrLow2=ebrRatio2!=null&&ebrRatio2<0.9;
      var ebrRedLow2=ebrRatio2!=null&&ebrRatio2<0.6;
      // Posted is the committed basis for the headline figure/color; pending fee (rarely rejected
      // in practice) is surfaced as a second angle in the tooltip/label rather than folded into the
      // headline number itself (Matt, 2026-08-21).
      var pendingFeeFB=phData.pendingFee||0;
      var feeExpendedInclPendingFB=phData.feeExpendedInclPending!=null?phData.feeExpendedInclPending:(phData.feeExpended+pendingFeeFB);
      if(budgetAlert==='over'){
        var overBy=phData.feeExpended-phData.contractedFee;
        var overByInclPending=feeExpendedInclPendingFB-phData.contractedFee;
        var alertColor=(ebrRedHigh2)?'var(--red)':'var(--amber)';
        alertEl.style.fontSize='9px'; alertEl.style.fontWeight='700';
        alertEl.style.color=alertColor;
        alertEl.textContent='⚠ Over budget by '+phFmtCurrency(overBy);
        var tipLine1='',tipLine2='';
        if(ebrRedHigh2){tipLine1='Budget exceeded · EBR high';tipLine2='Senior concentration is likely the primary cause';}
        else if(ebrHigh2){tipLine1='Budget exceeded · EBR elevated';tipLine2='High senior concentration is a contributing factor';}
        else if(ebrRedLow2){tipLine1='Budget exceeded · EBR low';tipLine2='Fee is significantly below the work delivered';}
        else if(ebrLow2){tipLine1='Budget exceeded · EBR below target';tipLine2='Consider whether the fee reflects the work delivered';}
        else{tipLine1='Budget exceeded · EBR healthy';tipLine2='Review scope, fee, and time allocation';}
        var infoIcon=document.createElement('span');
        infoIcon.className='mgr-budget-info';
        infoIcon.style.background=alertColor; infoIcon.style.color='white';
        infoIcon.textContent='i';
        var tip=document.createElement('span'); tip.className='mgr-budget-tip';
        var tipL1=document.createElement('div'); tipL1.style.cssText='font-weight:600;margin-bottom:3px;'; tipL1.textContent=tipLine1;
        var tipL2=document.createElement('div'); tipL2.style.fontWeight='400'; tipL2.textContent=tipLine2;
        tip.appendChild(tipL1); tip.appendChild(tipL2);
        if(pendingFeeFB>0){
          var tipL3=document.createElement('div');
          tipL3.style.cssText='font-weight:400;margin-top:3px;padding-top:3px;border-top:1px solid rgba(255,255,255,0.25);';
          tipL3.textContent='Posted-only. '+phFmtCurrency(overByInclPending)+' over including pending review.';
          tip.appendChild(tipL3);
        }
        infoIcon.appendChild(tip);
        alertEl.appendChild(infoIcon);
      } else {
        var remaining=phData.contractedFee-phData.feeExpended;
        var remainingInclPending=phData.contractedFee-feeExpendedInclPendingFB;
        alertEl.textContent='⚠ '+phFmtCurrency(remaining)+' remaining';
        if(pendingFeeFB>0){
          alertEl.textContent+=remainingInclPending>0
            ?' ('+phFmtCurrency(remainingInclPending)+' incl. pending)'
            :' (over by '+phFmtCurrency(-remainingInclPending)+' incl. pending)';
        }
        alertEl.style.fontSize='9px'; alertEl.style.fontWeight='700';
        alertEl.style.color='var(--amber)';
      }
      finBar.appendChild(alertEl);
    }
    return finBar;
  }

  // Extracted out of buildEntry so it can be rebuilt on demand for entries OTHER than the one just
  // approved (see refreshPhaseBudgetBars) — otherwise every sibling entry against the same phase
  // keeps showing the pre-approval fee/hours-remaining numbers until the list happens to fully
  // re-render (Matt, 2026-08-18: "other entries become stale after one is approved"). Tagged with
  // data-mbar-rid/data-mbar-phid so a refresh can find and replace the right node in place.
  function buildScheduleBar(r) {
    var f=r.fields;
    var project=Array.isArray(f['Project Name'])?f['Project Name'][0]:(f['Project Name']||'');
    var phRawId2=f['Phase Airtable ID'];
    var phId2=Array.isArray(phRawId2)?(phRawId2[0]&&phRawId2[0].id?phRawId2[0].id:phRawId2[0]):(phRawId2||'');
    var phData2=allPhases.find(function(p){return p.id===phId2;});
    var mBar=document.createElement('div');
    mBar.setAttribute('data-mbar-rid', r.id);
    if(phId2) mBar.setAttribute('data-mbar-phid', phId2);
    mBar.style.cssText='margin-top:4px;font-size:10px;font-weight:600;';
    // Mirrors phaseFinancials.js#computeLiveFinancials' isFixedFee exactly: Contract Type is
    // inconsistently populated (many admin/support-style phases — Permitting, Meetings,
    // Construction Support — leave it blank rather than set it to "Hourly"), so a phase only
    // gets Fixed-Fee treatment when it actually has a contracted amount to dilute against.
    // Fixed 2026-08-07 — Osborne Pismo Beach ADU's Permitting Phase (blank type, no fee) was
    // showing "Over by 46.4 hrs (46.4 of 0.0 budget)", a nonsense reading against a phantom
    // zero budget, because blank type used to default to Fixed-Fee treatment.
    var isPhaseHourly=phData2&&(phData2.contractType==='Hourly'||!(phData2.contractedFee>0));
    if(isPhaseHourly){
      var ebr=phData2.effectiveBillingRate;
      if(ebr!=null&&isFinite(ebr)&&ebr>0&&targetEBR>0){
        var ebrRatio=ebr/targetEBR;
        var ebrC=ebrColor(ebrRatio);
        var ebrL=ebrLabel(ebrRatio);
        var pct2=phData2.phaseWorkRemaining!=null?Math.round(phData2.phaseWorkRemaining*100):null;
        mBar.style.color=ebrC;
        var dot2=document.createElement('span');
        dot2.style.cssText='display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:4px;vertical-align:middle;background:'+ebrC+';';
        mBar.appendChild(dot2);
        var ebrStr='EBR $'+Math.round(ebr)+'/hr · '+(Math.round((ebrRatio-1)*100))+'% '+(ebrRatio>=1?'above':'below')+' target · '+ebrL;
        // "schedule:" prefix, not bare "% remaining" — same disambiguation as the Fixed-Fee
        // branch below: this is the milestone checklist, a different axis from EBR/$ (Matt, 2026-08-08).
        if(pct2===0) ebrStr+=' · schedule: all milestones posted';
        else if(pct2!==null) ebrStr+=' · schedule: '+pct2+'% of milestones still open';
        mBar.appendChild(document.createTextNode(ebrStr));
      }
    } else if(phData2){
      // Fee-based over/under is the PRIMARY Fixed-Fee signal — shown whenever there's a real
      // contracted fee, independent of whether Hours Budget could even be computed. Some phases
      // (Discretionary Approvals in particular, confirmed live 2026-08-08 on Forsyth Residence
      // and McMahon Workman) have no Blended Rate Rollup to derive an hours budget from, which
      // used to fall through to a bare "All milestones complete" — technically true, but it
      // silently dropped whether the phase was actually over budget. Milestone completion is
      // now a trailing note alongside the money, never a replacement for it (Matt, 2026-08-08):
      // knowing milestones are still open is useful context for how concerning a trend is, but
      // the $ over/under is what a reviewer actually needs to see every time.
      if(phData2.contractedFee>0&&phData2.feeExpended!=null){
        var feeOver=phData2.feeExpended-phData2.contractedFee;
        var isFeeOver=feeOver>0;
        var pendingFeeSB=phData2.pendingFee||0;
        var feeExpendedInclPendingSB=phData2.feeExpendedInclPending!=null?phData2.feeExpendedInclPending:(phData2.feeExpended+pendingFeeSB);
        var feeOverInclPending=feeExpendedInclPendingSB-phData2.contractedFee;
        var willTipOverInclPending=!isFeeOver&&feeOverInclPending>0;
        var hasHours=phData2.hoursBudget!=null&&phData2.hoursBudget>0&&phData2.hoursExpended!=null;
        var metricColor=isFeeOver?'var(--red)':(willTipOverInclPending?'var(--amber)':'var(--text-soft)');
        mBar.style.color=metricColor;
        var dot=document.createElement('span');
        dot.style.cssText='display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:4px;vertical-align:middle;background:'+metricColor+';';
        mBar.appendChild(dot);
        var feeStr=isFeeOver
          ?'⚠ Over budget by '+phFmtCurrency(feeOver)
          :phFmtCurrency(phData2.contractedFee-phData2.feeExpended)+' of fee remaining';
        if(hasHours){
          feeStr+=' ('+phData2.hoursExpended.toFixed(1)+' of '+phData2.hoursBudget.toFixed(1)+' hrs';
          // Posted-only Hours Expended can badly understate what's actually in flight — real,
          // already-logged hours sit in Pending Review/Submitted/Approved/Post Failed until the
          // next Core post, sometimes including the very entry being reviewed right now (Matt,
          // 2026-08-11). This never changes the $ basis above, just what's visibly not counted yet.
          if(phData2.pendingHours>0) feeStr+=' + '+phData2.pendingHours.toFixed(1)+' hrs pending review';
          feeStr+=')';
        }
        // Both angles, per Matt (2026-08-21): Posted-only stays the headline number (it's what's
        // actually committed via Core), but rejection is rare in practice, so whatever's sitting in
        // Pending Review/Submitted/Approved/Post Failed is close to a floor on what's coming, not a
        // hypothetical — worth showing right next to the posted figure rather than only as an hours
        // count.
        if(pendingFeeSB>0){
          feeStr+=isFeeOver
            ? ' → '+phFmtCurrency(feeOverInclPending)+' over incl. pending'
            : (willTipOverInclPending
                ? ' → would flip to '+phFmtCurrency(feeOverInclPending)+' over incl. pending'
                : ' → '+phFmtCurrency(-feeOverInclPending)+' remaining incl. pending');
        }
        // Labeled "schedule" (never bare "% remaining") on purpose — this is the planned
        // milestone checklist, not money. It's a different axis from the $ figure right before
        // it (a phase can read 0% schedule remaining while still over budget, or vice versa),
        // and without an explicit label that distinction is easy to lose on re-read weeks later
        // (Matt, 2026-08-08).
        if(phData2.phaseWorkRemaining===0) feeStr+=' · schedule: all milestones posted';
        else if(phData2.phaseWorkRemaining!=null) feeStr+=' · schedule: '+Math.round(phData2.phaseWorkRemaining*100)+'% of milestones still open';
        // Fixed-Fee budget runway — at the recent burn rate, when do hours cross the budget?
        // Only computable when Hours Budget itself is; Hourly has no comparable projection
        // (see computeLiveFinancials in phaseFinancials.js).
        if(!isFeeOver&&hasHours&&phData2.projectedBudgetDate){
          var projD=new Date(phData2.projectedBudgetDate+'T00:00:00');
          var daysOut=Math.round((projD.getTime()-new Date().setHours(0,0,0,0))/86400000);
          if(daysOut>0) feeStr+=' · at this pace, budget in ~'+daysOut+'d ('+projD.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})+')';
        }
        mBar.appendChild(document.createTextNode(feeStr));
      } else if(phData2&&!phData2.contractedFee){
        mBar.style.color='var(--amber)'; mBar.textContent='⚠ No contracted fee set';
      } else if(phData2&&phData2.phaseWorkRemaining==null){
        mBar.style.color='var(--amber)'; mBar.textContent='⚠ No milestones linked to phase';
      }
    }
    return (mBar.hasChildNodes()||mBar.textContent) ? mBar : null;
  }

  // What a posted entry moves from "pending" to "expended" on its phase — same field precedence
  // used everywhere else in this file (Client Hours overrides Actual Hours; Charge Amount already
  // follows that same precedence server-side, see docs/TIME_RECORDS.md).
  function phaseImpactFromRecord(r) {
    var f=(r&&r.fields)||{};
    var raw=f['Phase Airtable ID'];
    var phId=Array.isArray(raw)?(raw[0]&&raw[0].id?raw[0].id:raw[0]):(raw||'');
    var clientHours=(f['Client Hours']!=null)?Number(f['Client Hours']):null;
    var hours=clientHours!=null?clientHours:(parseFloat(f['Actual Hours'])||0);
    var fee=parseFloat(f['Charge Amount'])||0;
    return { phId: phId, hours: hours, fee: fee };
  }

  // Locally advances a phase's expended/pending figures the instant an entry's post to Core
  // succeeds. allPhases is only ever fetched at modal-open and via the manual "Sync Billed Fee"
  // button (see fetchPhases) — approving/posting an entry never refetches it on its own — so
  // without this, every OTHER pending entry against the same phase keeps reading the pre-approval
  // budget until the reviewer manually syncs or reopens the tool.
  function patchPhaseAfterPost(phId, entryHours, entryFee) {
    if(!phId) return;
    var idx=allPhases.findIndex(function(p){return p.id===phId;});
    if(idx===-1) return;
    var p=allPhases[idx];
    var hoursExpended=(p.hoursExpended||0)+(entryHours||0);
    var feeExpended=(p.feeExpended||0)+(entryFee||0);
    var pendingHours=Math.max(0,(p.pendingHours||0)-(entryHours||0));
    var pendingFee=Math.max(0,(p.pendingFee||0)-(entryFee||0));
    var hoursRemaining=p.hoursBudget!=null?p.hoursBudget-hoursExpended:null;
    var isFixedFee=isPhaseFixedFee(p);
    var effectiveBillingRate=hoursExpended>0
      ?(isFixedFee?(p.contractedFee>0?p.contractedFee/hoursExpended:null):(feeExpended/hoursExpended))
      :null;
    allPhases[idx]=Object.assign({},p,{
      hoursExpended:Math.round(hoursExpended*10)/10,
      feeExpended:Math.round(feeExpended*100)/100,
      pendingHours:Math.round(pendingHours*10)/10,
      pendingFee:Math.round(pendingFee*100)/100,
      feeExpendedInclPending:Math.round((feeExpended+pendingFee)*100)/100,
      hoursRemaining:hoursRemaining!=null?Math.round(hoursRemaining*10)/10:null,
      effectiveBillingRate:effectiveBillingRate,
    });
  }

  // Shared by refreshPhaseScheduleBars — swaps in a freshly-built bar for every OTHER
  // currently-rendered entry tagged against phId, in place. Never a full render() here, so the
  // entry that was just approved keeps its own "Posted to Core" confirmation animation running on
  // its own timer, undisturbed.
  function refreshTaggedBars(phIdAttr, ridAttr, phId, exceptRid, builder) {
    var olds=container.querySelectorAll('['+phIdAttr+'="'+phId+'"]');
    for(var i=0;i<olds.length;i++){
      var old=olds[i];
      var rid2=old.getAttribute(ridAttr);
      if(rid2===exceptRid) continue;
      var rec=allRecords.find(function(x){return x.id===rid2;})||needsAttentionRecords.find(function(x){return x.id===rid2;});
      if(!rec) continue;
      var fresh=builder(rec);
      if(fresh) old.parentNode.replaceChild(fresh,old);
      else if(old.parentNode) old.parentNode.removeChild(old);
    }
  }
  // Rebuilds BOTH phase-budget bars (the fee/schedule line and the Contract/Billed/EBR chip row)
  // for every other pending entry against the same phase, right after a post succeeds.
  function refreshPhaseScheduleBars(phId, exceptRid) {
    if(!phId) return;
    refreshTaggedBars('data-mbar-phid', 'data-mbar-rid', phId, exceptRid, buildScheduleBar);
    refreshTaggedBars('data-finbar-phid', 'data-finbar-rid', phId, exceptRid, buildFinBar);
  }

  function buildEntry(r) {
    var rid=r.id, f=r.fields;
    var postErrorRaw=f['Post Error']||'';
    var postErrorType='';
    if(postErrorRaw){
      var typeMatch=postErrorRaw.match(/"type"\s*:\s*"([^"]+)"/);
      if(typeMatch) postErrorType=typeMatch[1];
      else if(postErrorRaw.indexOf('HTTP 4')!==-1||postErrorRaw.indexOf('HTTP 5')!==-1){
        var codeMatch=postErrorRaw.match(/HTTP (\d+)/);
        postErrorType=codeMatch?'HTTP '+codeMatch[1]+' error':'Post error';
      } else { postErrorType='Post error'; }
    }
    var project=Array.isArray(f['Project Name'])?f['Project Name'][0]:(f['Project Name']||'');
    var phase=Array.isArray(f['Phase Name'])?f['Phase Name'][0]:(f['Phase Name']||'');
    var memo=f['Memo Draft']||'', hours=getHours(r);
    // BQE Core's clientHours — null means "no reviewer override, follows Actual Hours". See
    // docs/TIME_RECORDS.md. Only ever non-null once a reviewer has deliberately diverged it.
    var clientHoursOverride=(f['Client Hours']!=null)?Number(f['Client Hours']):null;
    var actRaw=f['Activity Code String'];
    var actCode=(actRaw?(Array.isArray(actRaw)?String(actRaw[0]||''):String(actRaw)):'' ).trim();
    var billable=isBillable(r);
    var status=getStatus(r), rejNote=f['Rejection Notes']||'';
    var isEditing=editingId===rid, isRejecting=rejectingId===rid;
    var isSelected=!!selectedIds[rid];

    var row=document.createElement('div');
    row.className='mgr-entry'+(isEditing?' is-editing':'')+(isRejecting?' is-rejecting':'')+(isSelected?' is-selected':'')+(status==='Post Failed'?' failed-row':'');
    row.setAttribute('data-rid', rid);
    // Clicking anywhere in the row also moves the ↑/↓ keyboard focus here — previously only
    // arrow keys could move it, so reaching a row far down the list meant arrowing through
    // every row in between instead of just clicking it. Harmless alongside the row's own
    // checkbox/link/action-button clicks, which keep their own handlers.
    row.addEventListener('click', function(){ setFocus(rid); });

    var cb=document.createElement('input'); cb.type='checkbox'; cb.className='mgr-cb mgr-entry-cb'; cb.checked=isSelected;
    cb.addEventListener('change',function(){ selectedIds[rid]=cb.checked; renderBatchBar(); });
    row.appendChild(cb);

    var body=document.createElement('div'); body.className='mgr-entry-body';
    var top=document.createElement('div'); top.className='mgr-entry-top';
    var info=document.createElement('div');
    var pn=document.createElement('div'); pn.className='mgr-entry-project';
    var projectUid=Array.isArray(f['Project Record UID'])?f['Project Record UID'][0]:(f['Project Record UID']||'');
    if(project&&projectUid){
      var pLink=document.createElement('a');
      pLink.href='https://app.alderjames.com/project-details?recordId='+encodeURIComponent(projectUid)+'#tab2';
      pLink.target='_blank'; pLink.rel='noopener noreferrer';
      pLink.textContent=project;
      pLink.style.cssText='color:var(--aj-text);text-decoration:none;font-weight:600;';
      pLink.addEventListener('mouseenter',function(){this.style.textDecoration='underline';});
      pLink.addEventListener('mouseleave',function(){this.style.textDecoration='none';});
      pn.appendChild(pLink);
    } else { pn.textContent=project||'No project'; }
    info.appendChild(pn);
    if(phase){
      var phEl=document.createElement('div'); phEl.className='mgr-entry-phase'; phEl.textContent=phase; info.appendChild(phEl);
      // Click the phase name → a live peek into the project's REAL Schedule branch (the actual
      // outline, in an iframe — see schedulePeek.js), so a reviewer can judge whether to move this
      // entry against the real phases/milestones/budget, not a rebuilt summary of them.
      if(projectUid){
        phEl.style.cursor='pointer'; phEl.title='Open the project’s Schedule';
        phEl.addEventListener('mouseenter',function(){phEl.style.textDecoration='underline';});
        phEl.addEventListener('mouseleave',function(){phEl.style.textDecoration='none';});
        phEl.addEventListener('click',function(e){ e.stopPropagation(); openSchedulePeek(projectUid, project); });
      }
      var finBar=buildFinBar(r);
      if(finBar) info.appendChild(finBar);
    }
    // Attributed work item (entity) — what this entry advanced, set by the AI parser. Read-only chip.
    var entType=(Array.isArray(f['Entity Type'])?f['Entity Type'][0]:f['Entity Type'])||'';
    var entName=(Array.isArray(f['Entity Name'])?f['Entity Name'][0]:f['Entity Name'])||'';
    if(entType){
      var TYPE_ABBR={'Drawing Sheet':'Sheet','BIM System':'BIM','Coordination Item':'Coord','Thread':'Thread','Deliverable':'Milestone','Agency Review':'Agency','Opportunity':'Opp','Lead':'Lead','Billing Run':'Billing'};
      var ent=document.createElement('div'); ent.className='mgr-entry-entity';
      ent.style.cssText='display:inline-flex;align-items:center;gap:5px;margin-top:3px;font-family:"DM Mono",monospace;font-size:10px;color:var(--aj-text-mid);';
      var tag=document.createElement('span');
      tag.style.cssText='background:var(--aj-bg-muted);color:var(--aj-text);border-radius:5px;padding:1px 6px;font-weight:600;letter-spacing:.02em;';
      tag.textContent=TYPE_ABBR[entType]||entType;
      ent.appendChild(tag);
      if(entName){ var en=document.createElement('span'); en.textContent=entName; ent.appendChild(en); }
      info.appendChild(ent);
    }
    // Skill credit preview — what demonstrated skill(s) this entry's hours build (activity + entity).
    var skActRaw=f['Activity Code String']; var skAct=(skActRaw?(Array.isArray(skActRaw)?String(skActRaw[0]||''):String(skActRaw)):'').trim();
    var skCredit=skillsForTimeEntry({activityCode:skAct, entityType:entType, entityName:entName, phaseName:f['Phase Name'], hours:hours});
    if(skCredit.length){
      var sk=document.createElement('div');
      sk.style.cssText='margin-top:3px;font-family:"DM Mono",monospace;font-size:10px;color:var(--aj-text-muted);';
      sk.textContent='Skill credit: +'+Number(hours).toFixed(1)+'h → '+skCredit.map(function(c){return c.skill;}).join(', ');
      info.appendChild(sk);
    }
    if(memo&&!isEditing){ var mm=document.createElement('div'); mm.className='mgr-entry-memo'; mm.textContent=memo; info.appendChild(mm); }
    var rcl=!isEditing&&reviewContextLine(r);
    if(rcl){ var rc=document.createElement('div'); rc.style.cssText='font-size:11px;margin-top:2px;color:'+(rcl.warn?'var(--amber,#b7791f)':'var(--aj-text-muted)'); rc.textContent=(rcl.warn?'⚠ ':'')+rcl.text; info.appendChild(rc); }

    if(phase&&project.indexOf('Alder James Overhead')===-1){
      var mBar=buildScheduleBar(r);
      if(mBar) info.appendChild(mBar);
    }
    if(rejNote&&status==='Rejected'){ var rn=document.createElement('div'); rn.className='mgr-rejection-note'; rn.textContent='Rejected: '+rejNote; info.appendChild(rn); }

    var meta=document.createElement('div'); meta.className='mgr-entry-meta';
    var hrs=document.createElement('span'); hrs.className='mgr-entry-hours'; hrs.textContent=hours.toFixed(2)+' hrs'; meta.appendChild(hrs);
    if(clientHoursOverride!=null && clientHoursOverride!==hours){
      var chBadge=document.createElement('span'); chBadge.className='mgr-clienthours-badge';
      chBadge.title='Client Hours (billed to BQE Core) differs from Actual Hours';
      chBadge.textContent='→ '+clientHoursOverride.toFixed(2)+' billed'; meta.appendChild(chBadge);
    }
    if(needsAttentionView){
      var entryDateStr=getEntryDate(r);
      if(entryDateStr){
        var entryDateObj=new Date(entryDateStr+'T12:00:00');
        var entryDateEl=document.createElement('span');
        entryDateEl.style.cssText='font-size:10px;font-family:DM Mono,monospace;color:var(--text-soft);font-weight:500;';
        entryDateEl.textContent=entryDateObj.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
        meta.appendChild(entryDateEl);
      }
    }
    var chargeAmt=parseFloat(f['Charge Amount'])||0;
    if(chargeAmt>0){
      var phRawId3=f['Phase Airtable ID'];
      var phId3=Array.isArray(phRawId3)?(phRawId3[0]&&phRawId3[0].id?phRawId3[0].id:phRawId3[0]):(phRawId3||'');
      var phData3=allPhases.find(function(p){return p.id===phId3;});
      var ebrRatio3=phData3&&phData3.effectiveBillingRate&&targetEBR?phData3.effectiveBillingRate/targetEBR:null;
      var tipData=ebrChargeTip(ebrRatio3, phData3, chargeAmt, hours);
      var cColor=ebrColor(ebrRatio3, isPhaseFixedFee(phData3));
      // An entry that itself pulls the phase's EBR back toward target is fine, even under a phase
      // whose OVERALL ratio is still flagged — color the entry green rather than inheriting the
      // phase's amber/red so the marker never contradicts the tooltip verdict right next to it
      // (Matt, 2026-08-12, spotted on Armando's $122/hr entry). Fixed-Fee phases never set
      // `direction` (every entry pushes the same way — see ebrChargeTip above), so this only
      // applies to Hourly.
      if(tipData && tipData.direction==='improves') cColor='var(--green)';
      var chargeWrap=document.createElement('span');
      chargeWrap.style.cssText='display:inline-flex;align-items:center;gap:3px;';
      var chargeEl=document.createElement('span');
      chargeEl.style.cssText='font-size:11px;font-family:DM Mono,monospace;font-weight:500;color:'+cColor+';';
      chargeEl.textContent='$'+chargeAmt.toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0});
      chargeWrap.appendChild(chargeEl);
      var tipTitle2=tipData?tipData.title:'No phase EBR available';
      var tipBody2=tipData?tipData.body:'Charge reflects hours × rate only. No phase or billing rate data to evaluate against.';
      var cInfoIcon=document.createElement('span');
      cInfoIcon.className='mgr-budget-info mgr-charge-tip-wrap';
      cInfoIcon.style.cssText='background:'+(tipData?cColor:'var(--text-soft)')+';color:white;width:13px;height:13px;font-size:9px;font-weight:700;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;cursor:default;position:relative;flex-shrink:0;margin-left:2px;';
      cInfoIcon.textContent='i';
      var cTip=document.createElement('span'); cTip.className='mgr-budget-tip';
      var cTipL1=document.createElement('div'); cTipL1.style.cssText='font-weight:600;margin-bottom:3px;'; cTipL1.textContent=tipTitle2;
      var cTipL2=document.createElement('div'); cTipL2.style.fontWeight='400'; cTipL2.textContent=tipBody2;
      cTip.appendChild(cTipL1); cTip.appendChild(cTipL2);
      cInfoIcon.appendChild(cTip);
      chargeWrap.appendChild(cInfoIcon);
      meta.appendChild(chargeWrap);
    }
    if(actCode){ var badge=document.createElement('span'); badge.className='mgr-entry-badge '+(billable?'badge-billable':'badge-overhead'); badge.textContent=actCode; meta.appendChild(badge); }
    var bilIndicator=document.createElement('span');
    bilIndicator.style.cssText='display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;';
    var bilDot=document.createElement('span');
    bilDot.style.cssText='width:7px;height:7px;border-radius:50%;flex-shrink:0;background:'+(billable?'var(--green)':'var(--border)')+';';
    bilIndicator.style.color=billable?'var(--green)':'var(--text-soft)';
    bilIndicator.appendChild(bilDot); bilIndicator.appendChild(document.createTextNode(billable?'Billable':'Non-billable'));
    meta.appendChild(bilIndicator);
    var pill=document.createElement('span'); pill.className='mgr-status-pill '+pillClass(status); pill.textContent=status==='Pending Review'?'Drafting':status; meta.appendChild(pill);
    // Read-only reference into the actual Core record — see docs/TIME_RECORDS.md's Core lock. Only
    // ever shown once posted; this app never writes back to a posted entry.
    var coreEntryId=f['Core Entry ID']||'';
    if(coreEntryId){
      var coreLink=document.createElement('a');
      coreLink.href='https://wap1-01.bqecore.com/webapp/TimeEntry/TimeEntryEdit/'+encodeURIComponent(coreEntryId);
      coreLink.target='_blank'; coreLink.rel='noopener noreferrer';
      coreLink.textContent='↗ Core';
      coreLink.title='Open this entry in BQE Core';
      coreLink.style.cssText='font-size:10px;color:var(--accent);text-decoration:none;';
      meta.appendChild(coreLink);
    }

    // Speech bubble → begin a Slack DM with the submitter, seeded with this entry's context.
    var submitterName=(f['Member Name']||'').split(' ')[0]||'the submitter';
    var dmBtn=document.createElement('button'); dmBtn.type='button'; dmBtn.className='mgr-dm-btn';
    dmBtn.title='Message '+submitterName+' in Slack about this entry';
    dmBtn.setAttribute('aria-label', dmBtn.title);
    dmBtn.innerHTML='<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
    dmBtn.addEventListener('click', function(e){
      e.stopPropagation();
      var memberId=getSubmitterId(r);
      if(!memberId){ alertDialog({ title:'No submitter', message:'This entry has no submitter on record.' }); return; }
      var dStr=getEntryDate(r);
      var proj=Array.isArray(f['Project Name'])?f['Project Name'][0]:(f['Project Name']||'');
      var ph=Array.isArray(f['Phase Name'])?f['Phase Name'][0]:(f['Phase Name']||'');
      var memo2=f['Memo Draft']||'';
      var seed='Quick question about your time entry'+(dStr?' — '+fmtDisplay(dStr):'')+' · '+getHours(r).toFixed(2)+'h'+(proj?' · '+proj:'')+(ph?' · '+ph:'')+(memo2?' — "'+memo2+'"':'')+' :';
      function getToken(){ return Promise.resolve(window.__clerkGetToken ? window.__clerkGetToken() : null); }

      function openDm(){
        dmBtn.disabled=true;
        return getToken().then(function(token){
          return startDirectMessage({ submitterMemberId: memberId, text: seed }, token);
        }).then(function(res){
          dmBtn.disabled=false;
          if(res && res.permalink){ window.open(res.permalink,'_blank','noopener'); }
          else if(res && res.deepLink){
            if(res.needsConnect){ try{ navigator.clipboard && navigator.clipboard.writeText(seed); }catch(e){} }
            window.open(res.deepLink,'_blank','noopener');
          }
          else if(res && res.needsSlackId){ promptForSlackId(res.memberName); }   // add it right here, then retry
          else { alertDialog({ title:'Could not open DM', message:(res && res.error) || 'Could not start a Slack DM.' }); }
        }).catch(function(){ dmBtn.disabled=false; alertDialog({ title:'Could not open DM', message:'Slack request failed.' }); });
      }

      function promptForSlackId(name){
        promptDialog({
          title:'Add Slack ID',
          message:'No Slack account is on file for '+(name||submitterName)+'. Enter their Slack member ID (starts with U) or @handle — it will be saved to their contact for next time.',
          placeholder:'U0123ABCDE  or  @firstname',
          required:true, confirmLabel:'Save & message',
        }).then(function(input){
          if(input===null) return;   // cancelled
          dmBtn.disabled=true;
          getToken().then(function(token){
            return setMemberSlackId({ submitterMemberId: memberId, input: input }, token);
          }).then(function(sr){
            dmBtn.disabled=false;
            if(sr && sr.slackUserId){ openDm(); }   // saved → open the DM now
            else { alertDialog({ title:'Could not save Slack ID', message:(sr && sr.error) || 'Enter the Slack member ID (starts with U) or an exact handle.' }); }
          }).catch(function(){ dmBtn.disabled=false; alertDialog({ title:'Could not save Slack ID', message:'Request failed.' }); });
        });
      }

      openDm();
    });
    meta.appendChild(dmBtn);

    top.appendChild(info); top.appendChild(meta); body.appendChild(top);

    if(isEditing){
      var ef=document.createElement('div'); ef.className='mgr-edit-form';
      if(status==='Posted'){
        var reviseNote=document.createElement('div');
        reviseNote.style.cssText='font-size:11px;color:var(--aj-text-mid);background:var(--aj-bg-muted);border-radius:4px;padding:7px 9px;margin-bottom:8px;line-height:1.5;';
        reviseNote.textContent='Not yet billed in Core — saving here updates both BQE Core and Cortex together.';
        ef.appendChild(reviseNote);
      }
      var hRow=document.createElement('div'); hRow.className='mgr-edit-hours-row';
      var hf=document.createElement('div'); hf.className='mgr-edit-field';
      var hl=document.createElement('label'); hl.textContent='Actual Hours';
      var hi=document.createElement('input'); hi.type='number'; hi.id='mgr-eh-'+rid; hi.value=hours; hi.step='0.1'; hi.min='0'; hi.max='24';
      hf.appendChild(hl); hf.appendChild(hi); hRow.appendChild(hf);
      // Client Hours (BQE Core's clientHours) — defaults to mirroring Actual Hours live until the
      // reviewer types a different number, at which point it "locks" to their override and stops
      // following. chDirty starts true if this entry already carries a stored override, since an
      // existing manual decision must not be silently overwritten by an unrelated Actual Hours edit.
      var chDirty=(clientHoursOverride!=null);
      var cf=document.createElement('div'); cf.className='mgr-edit-field';
      var cfLabelRow=document.createElement('div'); cfLabelRow.className='mgr-edit-ch-labelrow';
      var cl=document.createElement('label'); cl.textContent='Client Hours';
      cfLabelRow.appendChild(cl);
      var chResetBtn=document.createElement('button'); chResetBtn.type='button'; chResetBtn.className='mgr-ch-reset';
      chResetBtn.textContent='match actual'; chResetBtn.title='Clear the override — follow Actual Hours';
      chResetBtn.style.display=chDirty?'inline':'none';
      cfLabelRow.appendChild(chResetBtn);
      var ci=document.createElement('input'); ci.type='number'; ci.id='mgr-ech-'+rid; ci.step='0.1'; ci.min='0'; ci.max='24';
      ci.value=chDirty?clientHoursOverride:hours;
      ci.title="Hours billed to the client in BQE Core (Core's clientHours). Defaults to Actual Hours.";
      ci.addEventListener('input',function(){ chDirty=true; chResetBtn.style.display='inline'; });
      chResetBtn.addEventListener('click',function(){ chDirty=false; chResetBtn.style.display='none'; ci.value=hi.value; });
      hi.addEventListener('input',function(){ if(!chDirty) ci.value=hi.value; });
      cf.appendChild(cfLabelRow); cf.appendChild(ci); hRow.appendChild(cf);
      ef.appendChild(hRow);
      var af=document.createElement('div'); af.className='mgr-edit-field';
      var al=document.createElement('label'); al.textContent='Activity Code';
      var curActId=''; var actStr2=(f['Activity Code String']?(Array.isArray(f['Activity Code String'])?String(f['Activity Code String'][0]||''):String(f['Activity Code String'])):'' ).trim();
      if(actStr2){ var mb2=ACTIVITY_CODES.find(function(a){return a.label.trim()===actStr2;}); if(mb2) curActId=mb2.id; }
      var actItems=ACTIVITY_CODES.map(function(a){return{id:a.id,name:a.label};}).sort(function(a,b){return a.name.localeCompare(b.name);});
      var actSD=buildEditSD(af, actItems, curActId, 'Search activity codes...', function(v){ actSD._getValue=function(){return v;}; });
      actSD.id='mgr-ea-sd-'+rid;
      af.appendChild(al); af.appendChild(actSD); ef.appendChild(af);
      var pf=document.createElement('div'); pf.className='mgr-edit-field';
      var pl=document.createElement('label'); pl.textContent='Phase';
      var curPhId='';
      var phRaw=f['Phase Airtable ID'];
      if(phRaw){ if(Array.isArray(phRaw)&&phRaw[0]) curPhId=phRaw[0].id||phRaw[0]; else if(typeof phRaw==='string') curPhId=phRaw; }
      var phSD=buildEditSD(pf, allPhases, curPhId, 'No phase', function(v){ phSD._getValue=function(){return v;}; });
      phSD.id='mgr-ep-sd-'+rid;
      pf.appendChild(pl); pf.appendChild(phSD); ef.appendChild(pf);

      // Work item (entity attribution) — correct what the AI attributed this entry to. Dropdown is the
      // submitter's live work list (lazy-loaded + cached per submitter). '' clears the attribution.
      var ENT_ABBR={'Drawing Sheet':'Sheet','BIM System':'BIM','Coordination Item':'Coord','Thread':'Thread','Deliverable':'Milestone','Agency Review':'Agency','Opportunity':'Opp','Lead':'Lead','Billing Run':'Billing'};
      var curEntId=(Array.isArray(f['Entity Id'])?f['Entity Id'][0]:f['Entity Id'])||'';
      var curEntType=(Array.isArray(f['Entity Type'])?f['Entity Type'][0]:f['Entity Type'])||'';
      var curEntName=(Array.isArray(f['Entity Name'])?f['Entity Name'][0]:f['Entity Name'])||'';
      var entMemberId=getSubmitterId(r);
      var pickedEntId=curEntId, entItemsMap={};
      var entF=document.createElement('div'); entF.className='mgr-edit-field';
      var entL=document.createElement('label'); entL.textContent='Work item';
      var entSlot=document.createElement('div');
      entF.appendChild(entL); entF.appendChild(entSlot); ef.appendChild(entF);
      // Scope work items to the entry's phase/project (project work by project; overhead work units —
      // Opportunity/Lead → Business Development, Billing Run → Operations — by the phase's category).
      function buildEntityPicker(items){
        entItemsMap={};
        var sdItems=[{id:'',name:'— No work item —'}];
        var scoped=tc.scopeWorkItemsForEntry(items, { projectName: f['Project Name'], phaseName: f['Phase Name'] });
        scoped.forEach(function(it){
          entItemsMap[it.entity_id]={entity_type:it.entity_type,entity_name:it.entity_name};
          var ab=ENT_ABBR[it.entity_type]||it.entity_type;
          sdItems.push({id:it.entity_id,name:'['+ab+'] '+(it.entity_name||it.entity_id)+(it.project_name?' · '+it.project_name:'')});
        });
        // Keep the current attribution selectable even if the item has left the submitter's active inbox.
        if(curEntId&&!entItemsMap[curEntId]){
          entItemsMap[curEntId]={entity_type:curEntType,entity_name:curEntName};
          var ab2=ENT_ABBR[curEntType]||curEntType||'Item';
          sdItems.splice(1,0,{id:curEntId,name:'['+ab2+'] '+(curEntName||curEntId)+' (current)'});
        }
        entSlot.innerHTML=''; entSlot.style.cssText='';
        var entSD=buildEditSD(entSlot, sdItems, curEntId, 'No work item', function(v){ pickedEntId=v; });
        entSD.id='mgr-ent-sd-'+rid;
      }
      if(entMemberId&&workItemsCache[entMemberId]){ buildEntityPicker(workItemsCache[entMemberId]); }
      else if(entMemberId){
        entSlot.textContent='Loading work items…'; entSlot.style.cssText='font-size:11px;color:var(--aj-text-muted);padding:4px 0;';
        tc.getSubmitterWorkItems(entMemberId).then(function(items){
          workItemsCache[entMemberId]=items||[];
          if(editingId===rid) buildEntityPicker(workItemsCache[entMemberId]);
        }).catch(function(){ entSlot.textContent='Could not load work items.'; });
      } else { entSlot.textContent='No submitter on record.'; entSlot.style.cssText='font-size:11px;color:var(--aj-text-muted);padding:4px 0;'; }

      var bf=document.createElement('div'); bf.className='mgr-edit-field';
      var bl=document.createElement('label'); bl.textContent='Billable';
      var toggleWrap=document.createElement('div'); toggleWrap.className='mgr-billable-toggle-wrap';
      var toggleLabel=document.createElement('label'); toggleLabel.className='mgr-billable-toggle';
      toggleLabel.setAttribute('for','mgr-bil-'+rid);
      var toggleInput=document.createElement('input'); toggleInput.type='checkbox';
      toggleInput.id='mgr-bil-'+rid; toggleInput.checked=billable;
      var toggleTrack=document.createElement('span'); toggleTrack.className='mgr-toggle-track';
      toggleLabel.appendChild(toggleInput); toggleLabel.appendChild(toggleTrack);
      var toggleText=document.createElement('span');
      toggleText.className='mgr-toggle-label '+(billable?'is-on':'is-off');
      toggleText.textContent=billable?'Billable':'Non-billable';
      toggleInput.addEventListener('change',function(){
        toggleText.textContent=toggleInput.checked?'Billable':'Non-billable';
        toggleText.className='mgr-toggle-label '+(toggleInput.checked?'is-on':'is-off');
      });
      toggleWrap.appendChild(toggleLabel); toggleWrap.appendChild(toggleText);
      bf.appendChild(bl); bf.appendChild(toggleWrap); ef.appendChild(bf);
      var mf=document.createElement('div'); mf.className='mgr-edit-field';
      var ml=document.createElement('label'); ml.textContent='Memo';
      var mt=document.createElement('textarea'); mt.id='mgr-em-'+rid; mt.textContent=memo;
      mf.appendChild(ml); mf.appendChild(mt); ef.appendChild(mf);
      var ea=document.createElement('div'); ea.className='mgr-edit-actions';
      var sv=document.createElement('button'); sv.className='mgr-btn btn-save'; sv.textContent='Save';
      sv.addEventListener('click',function(){
        var nh=parseFloat(document.getElementById('mgr-eh-'+rid).value);
        // null ⇒ clear any override, follow Actual Hours. Only saved as a real number once the
        // reviewer has actually diverged it (chDirty) — this is what keeps "defaults to Actual
        // Hours always" true without needing to re-derive it at every read site.
        var nch=null;
        if(chDirty){
          nch=parseFloat(document.getElementById('mgr-ech-'+rid).value);
          if(isNaN(nch)||nch<0) return;
        }
        var actSdEl=document.getElementById('mgr-ea-sd-'+rid);
        var na=actSdEl?actSdEl._getValue():'';
        var phSdEl=document.getElementById('mgr-ep-sd-'+rid);
        var np=phSdEl?phSdEl._getValue():'';
        var nm=document.getElementById('mgr-em-'+rid).value.trim();
        var bilEl=document.getElementById('mgr-bil-'+rid);
        var nb=bilEl?bilEl.checked:billable;
        if(isNaN(nh)||nh<0) return;
        sv.disabled=true; sv.textContent='Saving…';
        // Keep a Post Failed entry as Post Failed on edit (don't bounce it to Submitted) so it stays in
        // the failed-posts view and its Retry Post button remains — you fix the cause, then retry inline.
        var newStatus=status;
        // Resolve the picked work item → type/name (cleared when '—No work item—' chosen).
        var entChanged=(pickedEntId||'')!==(curEntId||'');
        var nEntType='',nEntName='';
        if(pickedEntId){ var em=entItemsMap[pickedEntId]; nEntType=(em&&em.entity_type)||curEntType||''; nEntName=(em&&em.entity_name)||curEntName||''; }
        // Mirror the server's own charge_amount formula (database/157_time_entries_charge_amount_client_hours.sql:
        // bill_rate * COALESCE(client_hours, actual_hours)) so the card's bill amount is correct the
        // instant Save is clicked, not just after the delayed reload picks up the real server value.
        var billRate=parseFloat(f['Bill Rate'])||0;
        var effHours=(nch!=null?nch:nh);
        var newChargeAmt=nb?(billRate*effHours):0;
        var localPatch={'Actual Hours':nh,'Client Hours':nch,'Charge Amount':newChargeAmt,'Activity Code':na,'Phase Airtable ID':np,'Memo Draft':nm,'Billable':nb,'Status':newStatus,'Post Error':''};
        if(entChanged){ localPatch['Entity Type']=nEntType; localPatch['Entity Id']=pickedEntId||''; localPatch['Entity Name']=nEntName; }
        updateLocal(rid,localPatch);
        editingId=null;
        var patchFields={'Actual Hours':nh,'Client Hours':nch,'Memo Draft':nm,'Billable':nb};
        if(na && na!==curActId) patchFields['Activity Code']=[na];
        if(np && np!==curPhId) patchFields['Phase Airtable ID']=[np];
        if(entChanged){ patchFields['Entity Type']=nEntType; patchFields['Entity Id']=pickedEntId||''; patchFields['Entity Name']=nEntName; }
        if(status==='Post Failed'){ patchFields['Post Error']=''; }   // clear stale error; status stays Post Failed → Retry Post
        // A Posted row can only ever get here via the Revise button above, which already confirmed
        // (live, against Core) that it isn't billed yet — but the server re-checks live again on
        // this exact call, since a race between opening this form and clicking Save is real, not
        // hypothetical. reviseEntry PATCHes Core first, then Cortex; saveEdit refuses a Posted row
        // outright, which is why the two paths fork here instead of sharing one call.
        var saveCall = (status==='Posted') ? tc.reviseEntry(rid,patchFields) : atPatch(TBL_STAGING,rid,patchFields);
        saveCall
          .then(function(res){
            if(res && res.id){
              sv.style.background='#1e6b47'; sv.textContent='✓ Saved';
              lastActionTime=Date.now();
              setTimeout(function(){ editingId=null; loadAll(); },1200);
            } else {
              sv.style.background='#c0392b'; sv.textContent='Error'; sv.disabled=false;
              // The Core lock (api/time-entries.js) — an entry already in BQE Core can't be edited
              // here unless revised; a bare "Error" gives no clue why, so surface the server's own
              // reason for a locked/billed refusal or a Core-succeeded-Cortex-failed partial write.
              if(res && (res.locked || res.mirrorFailed)) {
                alertDialog({ title: res.billed ? 'Already billed in Core' : 'Could not save', message: res.error });
              }
            }
          })
          .catch(function(){ sv.style.background='#c0392b'; sv.textContent='Error'; sv.disabled=false; });
      });
      var cv=document.createElement('button'); cv.className='mgr-btn btn-cancel'; cv.textContent='Cancel';
      cv.addEventListener('click',function(){ editingId=null; render(); });
      ea.appendChild(sv); ea.appendChild(cv); ef.appendChild(ea);
      body.appendChild(ef);
    }

    if(isRejecting){
      var rf=document.createElement('div'); rf.className='mgr-reject-form';
      var rl=document.createElement('span'); rl.className='mgr-reject-label'; rl.textContent='Reason for rejection';
      var ri=document.createElement('textarea'); ri.className='mgr-reject-input'; ri.id='mgr-rn-'+rid; ri.placeholder='Explain what needs to be corrected…';
      var ra=document.createElement('div'); ra.className='mgr-reject-actions';
      var rc=document.createElement('button'); rc.className='mgr-btn btn-confirm-reject'; rc.textContent='Confirm Rejection';
      rc.addEventListener('click',function(){
        var note=document.getElementById('mgr-rn-'+rid).value.trim();
        rc.disabled=true; rc.textContent='Rejecting…';
        var entryRow=rc.closest('.mgr-entry');
        if(entryRow) entryRow.classList.add('is-rejecting');
        atPatch(TBL_STAGING,rid,{'Status':'Rejected','Rejection Notes':note}).then(function(){
          tc.notifyRejection(rid, note).catch(function(){});
          if(entryRow) entryRow.classList.add('approving-fade');
          setTimeout(function(){
            updateLocal(rid,{'Status':'Rejected','Rejection Notes':note});
            rejectingId=null;
            lastActionTime=Date.now();
            setTimeout(function(){ maybeReload(); },1500);
            render();
          },400);
        }).catch(function(){
          if(entryRow) entryRow.classList.add('approving-fade');
          setTimeout(function(){
            updateLocal(rid,{'Status':'Rejected','Rejection Notes':note});
            rejectingId=null;
            lastActionTime=Date.now();
            render();
          },400);
        });
      });
      var rca=document.createElement('button'); rca.className='mgr-btn btn-cancel'; rca.textContent='Cancel';
      rca.addEventListener('click',function(){ rejectingId=null; render(); });
      ra.appendChild(rc); ra.appendChild(rca);
      rf.appendChild(rl); rf.appendChild(ri); rf.appendChild(ra);
      body.appendChild(rf);
    }

    var actions=document.createElement('div'); actions.className='mgr-entry-actions';
    var p2=document.createElement('span'); p2.className='mgr-status-pill '+pillClass(status); p2.textContent=status==='Pending Review'?'Drafting':status; actions.appendChild(p2);

    if(!isEditing&&!isRejecting){
      if(postingIds[rid]){
        var ab2=document.createElement('button'); ab2.className='mgr-btn btn-approve'; ab2.disabled=true; ab2.textContent='Approved';
        var ps2=document.createElement('span'); ps2.className='bqe-post-status posting'; ps2.style.display='inline-flex';
        var sp2=document.createElement('span'); sp2.className='bqe-post-spinner';
        ps2.appendChild(sp2); ps2.appendChild(document.createTextNode(' Sending to BQE Core…'));
        actions.appendChild(ab2); actions.appendChild(ps2);
      } else if(status==='Submitted' && tc.activityCodeProblem(f['Phase Name'], f['Activity Code String'])){
        // Core would refuse this code for this project — fix it here instead of after a failed sync.
        var cw=document.createElement('span'); cw.style.cssText='font-size:11px;color:var(--red,#c0392b);max-width:340px;';
        cw.textContent='⚠ '+tc.activityCodeProblem(f['Phase Name'], f['Activity Code String'])+' Edit the entry to approve.';
        actions.appendChild(cw);
        var eb0=document.createElement('button'); eb0.className='mgr-btn btn-edit'; eb0.textContent='Edit';
        eb0.addEventListener('click',function(){ editingId=rid; rejectingId=null; render(); });
        actions.appendChild(eb0);
        var rb0=document.createElement('button'); rb0.className='mgr-btn btn-reject'; rb0.textContent='Reject';
        rb0.addEventListener('click',function(){ rejectingId=rid; editingId=null; render(); });
        actions.appendChild(rb0);
      } else if(status==='Submitted'){
        var ab=document.createElement('button'); ab.className='mgr-btn btn-approve'; ab.textContent='Approve';
        var postStatus=document.createElement('span'); postStatus.className='bqe-post-status'; postStatus.style.display='none';
        ab.addEventListener('click',function(){
          ab.classList.add('flashing'); ab.disabled=true; ab.textContent='Approving…';
          var entryRow=ab.closest('.mgr-entry');
          if(entryRow) entryRow.classList.add('approving');
          postingIds[rid]=true;
          atPatch(TBL_STAGING,rid,{'Status':'Approved'}).then(function(){
            ab.textContent='Approved';
            postStatus.style.display='inline-flex';
            postStatus.className='bqe-post-status posting';
            var spin=document.createElement('span'); spin.className='bqe-post-spinner';
            postStatus.innerHTML=''; postStatus.appendChild(spin);
            postStatus.appendChild(document.createTextNode(' Sending to BQE Core…'));
            return tc.postTimeEntryToCore(rid);
          }).then(function(result){
            delete postingIds[rid];
            if(result&&result.success){
              postStatus.className='bqe-post-status posted';
              postStatus.innerHTML='✓ Posted to Core';
              updateLocal(rid,{'Status':'Posted'});
              lastActionTime=Date.now();
              // Patch this phase's budget locally + repaint every OTHER pending entry against it
              // right now — don't make a reviewer wait for this row's own fade/reload timers to
              // see the impact on the next entry they're weighing (Matt, 2026-08-18).
              var approvedImpact=phaseImpactFromRecord(r);
              patchPhaseAfterPost(approvedImpact.phId, approvedImpact.hours, approvedImpact.fee);
              refreshPhaseScheduleBars(approvedImpact.phId, rid);
              setTimeout(function(){
                if(entryRow) entryRow.classList.add('approving-fade');
                setTimeout(function(){ maybeReload(); render(); },400);
              },2200);
            } else {
              postStatus.className='bqe-post-status post-failed';
              postStatus.innerHTML='⚠ Review before retrying';
              if(entryRow) entryRow.classList.remove('approving');
              updateLocal(rid,{'Status':'Post Failed'});
              lastActionTime=Date.now();
              render();
            }
          }).catch(function(){
            delete postingIds[rid];
            postStatus.className='bqe-post-status post-failed';
            postStatus.innerHTML='⚠ Network error — review entry';
            if(entryRow) entryRow.classList.remove('approving');
            updateLocal(rid,{'Status':'Post Failed'});
            lastActionTime=Date.now();
            render();
          });
        });
        actions.appendChild(ab);
        actions.appendChild(postStatus);
        var rb=document.createElement('button'); rb.className='mgr-btn btn-reject'; rb.textContent='Reject';
        rb.addEventListener('click',function(){ rejectingId=rid; editingId=null; render(); });
        actions.appendChild(rb);
        var eb=document.createElement('button'); eb.className='mgr-btn btn-edit'; eb.textContent='Edit';
        eb.addEventListener('click',function(){ editingId=rid; rejectingId=null; render(); });
        actions.appendChild(eb);
        // Hold for onboarding (database/308): parked on a Lead/Opportunity, stops blocking the review
        // lockout for 14 days while the project is onboarded. Approving clears it.
        var holdUntil=f['Hold Until']||'';
        if(holdUntil && new Date(holdUntil)>new Date()){
          var hn=document.createElement('span'); hn.style.cssText='font-size:11px;color:var(--aj-text-muted);';
          hn.textContent='Held for '+(f['Hold Ref Name']||'onboarding')+' until '+new Date(holdUntil).toLocaleDateString('en-US',{month:'short',day:'numeric'});
          actions.appendChild(hn);
          var rh=document.createElement('button'); rh.className='mgr-btn btn-edit'; rh.textContent='Release hold';
          rh.addEventListener('click',function(){
            rh.disabled=true;
            tc.releaseHold(rid).then(function(d){
              if(d&&d.error){ rh.disabled=false; return alertDialog({title:'Could not release it',message:d.error}); }
              updateLocal(rid,{'Hold Until':'','Hold Ref Name':'','Hold Kind':''}); render();
            });
          });
          actions.appendChild(rh);
        } else {
          var hb=document.createElement('button'); hb.className='mgr-btn btn-edit'; hb.textContent='Hold for onboarding';
          hb.title='Waiting on a project that is not set up yet. Holds it for 14 days so it stops blocking your review.';
          hb.addEventListener('click',function(){
            var memberName=(f['Member Name']||'');
            import('./HoldForOnboardingPicker.jsx').then(function(m){
              return m.openHoldPicker({ memberName: memberName, hours: f['Actual Hours'] });
            }).then(function(pick){
              if(!pick) return;
              hb.disabled=true; hb.textContent='Holding…';
              return tc.holdEntry(rid,pick).then(function(d){
                if(d&&d.error){ hb.disabled=false; hb.textContent='Hold for onboarding'; return alertDialog({title:'Could not hold it',message:d.error}); }
                var row=d&&d.row||{};
                updateLocal(rid,{'Hold Until':row.hold_until||'','Hold Ref Name':row.hold_ref_name||pick.refName,'Hold Kind':row.hold_kind||pick.kind});
                lastActionTime=Date.now(); render();
              });
            });
          });
          actions.appendChild(hb);
        }
      } else {
        if(status==='Pending Review'){
          var dn=document.createElement('span'); dn.style.fontSize='11px'; dn.style.color='var(--aj-text-muted)'; dn.style.fontStyle='italic'; dn.textContent='Not yet submitted'; actions.appendChild(dn);
        }
        if(status==='Approved'){
          var an=document.createElement('span'); an.style.fontSize='11px'; an.style.color='var(--green)'; an.textContent='✓ Approved'; actions.appendChild(an);
        }
        if(status==='Posted'){
          var pn2=document.createElement('span'); pn2.style.fontSize='11px'; pn2.style.color='var(--aj-text-muted)'; pn2.textContent='Posted to BQE'; actions.appendChild(pn2);
          // Revise — the one path back into a Posted entry, but only while BQE Core still shows it
          // as unbilled WIP. Checked live BEFORE the edit form ever opens (docs/TIME_RECORDS.md);
          // an already-billed entry never sees an editable field here, only Flag for correction.
          var revBtn=document.createElement('button'); revBtn.className='mgr-btn btn-edit'; revBtn.textContent='Revise';
          revBtn.addEventListener('click',function(){
            revBtn.disabled=true; revBtn.textContent='Checking Core…';
            tc.getCoreBillStatus(rid).then(function(st){
              revBtn.disabled=false; revBtn.textContent='Revise';
              if(st && st.billed){
                alertDialog({
                  title:'Already billed in Core',
                  message:'This entry has already been billed on invoice '+(st.invoiceNumber||st.invoiceId)+
                    ' in BQE Core. Billed entries can’t be revised here — flag it for correction instead, and fix the invoice in Core.',
                });
                return;
              }
              editingId=rid; rejectingId=null; render();
            }).catch(function(e){
              revBtn.disabled=false; revBtn.textContent='Revise';
              alertDialog({ title:'Could not check BQE Core', message:(e&&e.message)||'Try again.' });
            });
          });
          actions.appendChild(revBtn);
        }
        if(status==='Post Failed'){
          var retryBtn=document.createElement('button'); retryBtn.className='mgr-btn btn-amber'; retryBtn.textContent='Retry Post';
          retryBtn.addEventListener('click',function(){
            retryBtn.disabled=true;
            while(retryBtn.firstChild) retryBtn.removeChild(retryBtn.firstChild);
            var rSpin=document.createElement('span'); rSpin.className='mgr-fetch-spinner';
            rSpin.style.cssText='display:inline-block;margin-right:5px;vertical-align:middle;';
            retryBtn.appendChild(rSpin); retryBtn.appendChild(document.createTextNode('Retrying…'));
            tc.postTimeEntryToCore(rid).then(function(result){
              while(retryBtn.firstChild) retryBtn.removeChild(retryBtn.firstChild);
              if(result&&result.success){
                retryBtn.textContent='✓ Posted — refreshing…';
                retryBtn.style.background='#2d8a5e'; retryBtn.style.color='white'; retryBtn.style.borderColor='#2d8a5e';
                updateLocal(rid,{'Status':'Posted'});
                lastActionTime=Date.now();
                var retryImpact=phaseImpactFromRecord(r);
                patchPhaseAfterPost(retryImpact.phId, retryImpact.hours, retryImpact.fee);
                refreshPhaseScheduleBars(retryImpact.phId, rid);
                setTimeout(function(){ if(!editingId&&!rejectingId) loadAll(); },2500);
              } else {
                retryBtn.textContent='Failed again — edit or retry'; retryBtn.disabled=false;
              }
            }).catch(function(){
              while(retryBtn.firstChild) retryBtn.removeChild(retryBtn.firstChild);
              retryBtn.textContent='Network error — retry'; retryBtn.disabled=false;
            });
          });
          actions.appendChild(retryBtn);
          var pfEditBtn=document.createElement('button'); pfEditBtn.className='mgr-btn btn-edit'; pfEditBtn.textContent='Edit';
          pfEditBtn.addEventListener('click',function(){ editingId=rid; rejectingId=null; render(); });
          actions.appendChild(pfEditBtn);
          var pfDelBtn=document.createElement('button'); pfDelBtn.className='mgr-btn btn-reject'; pfDelBtn.textContent='Delete';
          pfDelBtn.addEventListener('click',async function(){
            if(await confirmDialog({ title:'Delete this entry?', message:'This cannot be undone.', confirmLabel:'Delete', danger:true })){
              updateLocal(rid,{'Status':'Deleted'});
              atDelete(TBL_STAGING,rid).catch(function(){});
              lastActionTime=Date.now();
              setTimeout(function(){ maybeReload(); },12000); render();
            }
          });
          actions.appendChild(pfDelBtn);
          var failNote=document.createElement('span'); failNote.style.fontSize='11px'; failNote.style.color='var(--amber)'; failNote.style.marginLeft='6px';
          failNote.textContent=postErrorType?'⚠ '+postErrorType+' — edit or retry':'Failed to post — edit or retry'; actions.appendChild(failNote);
        }
      }
    }
    body.appendChild(actions);
    row.appendChild(body);
    return row;
  }

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  // ↑/↓ move the focused entry; a = Approve, r = Reject, e = Edit the focused entry. Forms that open
  // in response (reject note / edit fields) get focus on their first input so you stay on the keyboard.
  // Tab is trapped inside the modal (so it lands on the modal's own action buttons, never the page
  // behind it), and stray single-key shortcuts are swallowed so nothing changes in the background.
  var overlay = (container.closest && container.closest('.tr-overlay')) || container;

  function visibleEntryEls(){
    return Array.prototype.filter.call(container.querySelectorAll('.mgr-entry'), function(el){ return el.offsetParent !== null; });
  }
  // Only a keyboard move (or the approve-and-advance step) scrolls the list. A redraw must never
  // scroll: redraws come from background loads (expenses, review context, the 2-minute refresh), and
  // scrolling there yanked the reviewer back to the last focused entry mid-scroll (Matt, 2026-10-28).
  function applyFocus(scroll){
    var els = container.querySelectorAll('.mgr-entry');
    for(var i=0;i<els.length;i++) els[i].classList.remove('kb-focus');
    if(!focusedRid){
      // Gate launch: once entries are on screen, auto-select the top one (no click needed).
      if(!autoExpand) return;
      var v = visibleEntryEls(); if(!v.length) return;
      focusedRid = v[0].getAttribute('data-rid');
    }
    var el = findByRid(focusedRid);
    if(el && el.offsetParent!==null){ el.classList.add('kb-focus'); if(scroll){ try{ el.scrollIntoView({block:'nearest'}); }catch(e){} } }
  }
  function setFocus(rid){ focusedRid = rid; applyFocus(true); }
  function moveFocus(dir){
    var list = visibleEntryEls(); if(!list.length) return;
    var idx = -1;
    for(var i=0;i<list.length;i++){ if(list[i].getAttribute('data-rid')===focusedRid){ idx=i; break; } }
    var next = idx===-1 ? (dir>0?0:list.length-1) : Math.max(0, Math.min(list.length-1, idx+dir));
    setFocus(list[next].getAttribute('data-rid'));
  }
  function actOnFocused(kind){
    if(!focusedRid){ var v=visibleEntryEls(); if(v.length) setFocus(v[0].getAttribute('data-rid')); return; }
    var el = findByRid(focusedRid); if(!el) return;
    var sel = kind==='approve' ? '.btn-approve' : kind==='reject' ? '.btn-reject' : '.btn-edit';
    var btn = el.querySelector(sel);
    if(!btn || btn.disabled) return;
    if(kind==='approve'){
      // Move focus to the next entry so a run of approvals stays on the keyboard.
      var list=visibleEntryEls(); var idx=list.indexOf(el);
      var nextEl=list[idx+1]||list[idx-1]||null;
      var nextRid=nextEl?nextEl.getAttribute('data-rid'):null;
      btn.click();
      if(nextRid) focusedRid=nextRid;
      setTimeout(function(){ applyFocus(true); },0);
    } else {
      btn.click();   // sets editingId/rejectingId then render() rebuilds the list with the form
      setTimeout(function(){
        var inp = kind==='reject' ? container.querySelector('#mgr-rn-'+focusedRid) : container.querySelector('#mgr-eh-'+focusedRid);
        if(inp){ try{ inp.focus(); if(inp.select) inp.select(); }catch(e){} }
        applyFocus(true);
      },0);
    }
  }
  function trapTab(e){
    var nodes = overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    var list = Array.prototype.filter.call(nodes, function(el){ return el.offsetParent!==null && !el.disabled; });
    if(!list.length) return;
    var first=list[0], last=list[list.length-1], ae=document.activeElement;
    if(e.shiftKey){ if(ae===first || !overlay.contains(ae)){ e.preventDefault(); last.focus(); } }
    else { if(ae===last || !overlay.contains(ae)){ e.preventDefault(); first.focus(); } }
  }
  function onKbDown(e){
    if(!document.body.contains(overlay)) return;
    if(e.key==='Tab'){ trapTab(e); return; }              // keep Tab inside the reviewer
    var ae=document.activeElement;
    var typing = ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName);
    if(e.key==='Enter'){
      // Tabbed-to buttons are createElement('button') (default type=submit, no form) → Enter doesn't
      // reliably fire a click. Do it explicitly so Enter activates the focused button.
      if(!typing && ae && ae.tagName==='BUTTON' && overlay.contains(ae)){ e.preventDefault(); e.stopPropagation(); ae.click(); }
      return;
    }
    if(typing) return;                                     // don't hijack typing or field Esc
    if(e.key==='ArrowDown'){ e.preventDefault(); e.stopPropagation(); moveFocus(1); }
    else if(e.key==='ArrowUp'){ e.preventDefault(); e.stopPropagation(); moveFocus(-1); }
    else if(e.key==='a'||e.key==='A'){ e.preventDefault(); e.stopPropagation(); actOnFocused('approve'); }
    else if(e.key==='r'||e.key==='R'){ e.preventDefault(); e.stopPropagation(); actOnFocused('reject'); }
    else if(e.key==='e'||e.key==='E'){ e.preventDefault(); e.stopPropagation(); actOnFocused('edit'); }
    else if(e.key!=='Escape' && e.key.length===1){ e.stopPropagation(); }  // swallow stray bg shortcuts (e.g. '/')
  }
  document.addEventListener('keydown', onKbDown, true);

  // ── Boot ──
  var _bootMon = getMonday(new Date());
  // Honor a preset date range (the gate's "Retry in Time Review" passes the oldest offending date so
  // older entries — e.g. a Post Failed from weeks ago — load); otherwise default to the current week.
  filterDateFrom = (preset && preset.from) ? preset.from : fmtDate(_bootMon);
  filterDateTo   = (preset && preset.to)   ? preset.to   : fmtDate(addDays(_bootMon, 6));
  function canReload(){ return !editingId&&!rejectingId&&selectedCount()===0&&Object.keys(postingIds).length===0&&(Date.now()-lastActionTime>ACTION_GRACE); }
  function maybeReload(){ if(canReload()){ needsAttentionView?loadNeedsAttention():loadAll(); } }

  fetchActivities();
  fetchSettings();
  fetchBilledFeeStatus();
  // Load this reviewer's responsibility scope (assignees + unassigned-if-admin) so the list, the team
  // overview, the member filter, and the Needs Attention badge only ever show their people.
  (function(){
    var myId = ctx.currentMember && ctx.currentMember.id;
    if (!myId) { reviewScope.loaded = true; render(); return; }
    getMyReviewScope(myId).then(function(s){
      reviewScope = { reviewees: new Set(s.reviewees || []), coveredAll: new Set(s.coveredAll || []), isAdmin: !!s.isAdmin, loaded: true };
      render();
    }).catch(function(){ reviewScope.loaded = true; render(); });
  })();
  fetchPhases().then(function(){ loadAll(); }).catch(function(){ loadAll(); });
  // Gate launch (preset member/status/date) stays in the normal filtered view: loadAll above honors
  // the preset date range + Staff/Status filters, so the offending entries (incl. Post Failed) surface.
  var pollId = setInterval(function(){ maybeReload(); }, 120000);
  function onWindowFocus(){ maybeReload(); }
  window.addEventListener('focus', onWindowFocus);

  return function cleanup(){
    document.removeEventListener('click', onDocClickCloseMS);
    document.removeEventListener('keydown', onKbDown, true);
    window.removeEventListener('focus', onWindowFocus);
    clearInterval(pollId);
  };
}
